import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useForm } from 'react-hook-form';
import {
  Box, Paper, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow,
  useMediaQuery, useTheme,
} from '@mui/material';

// Icons only — SVG, no emotion overhead
import PrintIcon from '@mui/icons-material/Print';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import TodayIcon from '@mui/icons-material/Today';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import RefreshIcon from '@mui/icons-material/Refresh';

import Loading from 'components/Loading';
import { timeStampMontlyReport, hrReportFullPDF, laborProjectReceiptPDF } from '../../components/PDF';
import AttendanceRulesPanel from '../../components/Card/AttendanceRulesPanel';
import WindowFrame from '../../components/Card/WindowFrame';
import * as actions from '../../redux/actions';
import TimestampProjectCost from '../../components/Table/TimestampProjectCost';
import MergedPayrollTableV2 from '../../components/Table/MergedPayrollTableV2';
import OtTimestampSummaryTable from '../../components/Table/OtTimestampSummaryTable';
import { computeProjectCostRows } from '../../components/Table/payroll/projectCostFormula';
import { sumCarryOut } from '../../components/Table/payroll/table/cells';
import HrSalaryDetail from './HrSalaryDetail';
import CarryoverDialog from '../../components/Card/CarryoverDialog';
import CarryoverPromptDialog from '../../components/Card/CarryoverPromptDialog';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import {
  TIMESTAMP_ALL,
  TIMESTAMP_PAYROLL_ALL,
  MANUAL_WORK_ALL,
  LEAVEREQUEST_ALL,
  PAYROLL_ADJ_ALL,
} from '../../redux/actions/types';

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

// เขตเวลาไทยตายตัว — ห้ามพึ่ง timezone ของเครื่อง/เบราว์เซอร์ผู้ใช้เฉยๆ (เดิม computeRange ด้านล่างใช้
// dayjs() เปล่าๆ ซึ่งอิง local timezone ของเครื่องที่รันตรงๆ ถ้าเครื่องคนละเครื่องตั้ง timezone ไม่ตรงกัน
// เช่น เครื่องนึงเป็น UTC อีกเครื่องเป็นเวลาไทย ขอบเขตงวดที่คำนวณได้จะเพี้ยนไปสูงสุด 7 ชม. — เช็คอินช่วง
// เที่ยงคืนตรงขอบงวดจะถูกนับผิดงวด ทำให้ยอดที่คำนวณจากเครื่องคนละเครื่อง (เช่น ปุ่ม "รีเซ็ต" ใน
// ExpensesTabLabor.js ที่ mount HrReportV2 headless vs หน้านี้ที่ user เปิดเองปกติ) ไม่ตรงกัน — บั๊กคลาส
// เดียวกับที่เพิ่งแก้ฝั่ง backend ไปแล้วใน timestamp.service.js (SSO cross-period check)
const THAI_TZ = 'Asia/Bangkok';

// ── static style constants (defined outside component — no re-creation per render) ──
const DIV = 'rgba(0,0,0,0.12)';
const PRIMARY = '#1976d2';
const TEXT = 'rgba(0,0,0,0.87)';
const TEXT2 = 'rgba(0,0,0,0.54)';
const BG = '#fafafa';
const BG2 = '#f5f5f5';

const LEAVE_TYPE_LABEL = {
  SICK: 'ลาป่วย',
  PERSONAL: 'ลากิจ',
  VACATION: 'ลาพักร้อน',
  MATERNITY: 'ลาคลอด',
  ORDINATION: 'ลาบวช',
  UNPAID: 'ลาไม่รับค่าจ้าง',
};
const LEAVE_COLOR_CHIP = {
  SICK: '#ec4899',
  PERSONAL: '#7c3aed',
  VACATION: '#059669',
  MATERNITY: '#d97706',
  ORDINATION: '#ea580c',
  UNPAID: '#64748b',
};

const S = {
  panel: { borderRadius: 4, overflow: 'hidden', border: `1px solid ${DIV}` },
  panelHead: { padding: 8, backgroundColor: BG, borderBottom: `1px solid ${DIV}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  row: { display: 'flex', alignItems: 'center', gap: 8 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    border: `1px solid ${DIV}`, borderRadius: 16, padding: '2px 8px',
    fontSize: '0.75rem', cursor: 'pointer', background: 'none', lineHeight: 1.5,
  },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 12px', borderRadius: 4, border: 'none',
    backgroundColor: PRIMARY, color: '#fff', cursor: 'pointer',
    fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.75,
  },
  btnSecondary: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 12px', borderRadius: 4, border: `1px solid ${PRIMARY}`,
    backgroundColor: '#fff', color: PRIMARY, cursor: 'pointer',
    fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.75,
  },
  btnIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: 4, border: 'none', borderRadius: 4, background: 'transparent',
    cursor: 'pointer', color: TEXT2,
  },
  select: {
    height: 36, borderRadius: 4, border: `1px solid ${DIV}`,
    padding: '0 8px', fontSize: '0.875rem', cursor: 'pointer',
    background: '#fff', color: TEXT, outline: 'none',
  },
  th: { padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: `1px solid ${DIV}`, whiteSpace: 'nowrap' },
  td: { padding: '4px 8px', fontSize: '0.8rem' },
  tdc: { padding: '4px 8px', fontSize: '0.78rem', textAlign: 'center' },
};

const STATUS_COLOR = {
  error: { color: '#d32f2f', borderColor: '#d32f2f' },
  success: { color: '#2e7d32', borderColor: '#2e7d32' },
  warning: { color: '#ed6c02', borderColor: '#ed6c02' },
  default: { color: TEXT2, borderColor: DIV },
};

const MONTH_NAMES = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน',
  'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม',
  'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

// สไตล์เซลล์ตารางใน modal "สรุปการเบิก CRL" (MUI Table)
const CRL_TH_SX = {
  fontWeight: 700,
  fontSize: '0.72rem',
  color: 'text.secondary',
  bgcolor: '#f8fafc',
  borderBottom: '2px solid',
  borderColor: 'divider',
  py: 0.75,
  whiteSpace: 'nowrap',
};
const CRL_TD_SX = {
  fontSize: '0.8rem',
  py: 0.6,
  borderColor: 'divider',
};

const TYPE_TABS = [
  { value: 'DAILY', label: 'พนักงานรายวัน', icon: WbSunnyRoundedIcon, gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)' },
  { value: 'FULLTIME', label: 'พนักงานรายเดือน', icon: CalendarMonthRoundedIcon, gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' },
];

// ── Cache ข้อมูลงวด (เฉพาะโหมดปกติ ไม่ lockPeriod) — ตัวแปรระดับ module ไม่ใช่ localStorage ──
// อยู่ได้แค่ระหว่าง session (สลับเมนูไปมาใน SPA ยังอยู่) แต่หายเองทันทีที่ refresh หน้าเว็บ (ตามต้องการ)
// key: `${typeSelect}|${dateStart}|${dateEnd}` — 3 ตัวนี้กำหนดผลลัพธ์ API ทั้งหมด (filter อื่นเป็น client-side)
const hrReportDataCache = new Map();
const HR_REPORT_CACHE_TTL = 8 * 60 * 1000; // 8 นาที — พ้นนี้ถือว่าเก่า โหลดใหม่อัตโนมัติ
// จำงวด/ประเภทพนักงานล่าสุดที่เปิดดูแบบปกติ (ไม่นับ instance ที่ถูกสั่ง initial*/lockPeriod มาจาก parent
// เช่น modal ตรวจสอบเวลาเข้างาน) ใช้ตอนกลับมาหน้า /humen/hr-report-v2 ในเซสชันเดียวกัน
let hrReportLastView = null;

// stub ว่างของ payrollAdj ตอน lockPeriod (ดู payrollAdj ด้านล่าง) — ต้องเป็นค่าคงที่นอก component
// ห้ามเขียน `{ rows: [] }` ตรงๆ ในตัว component เพราะจะสร้าง object ใหม่ทุก render ทำให้ useEffect
// ที่มี payrollAdj เป็น dependency คิดว่าเปลี่ยนตลอด รันซ้ำไม่หยุด (infinite render loop จนเว็บค้าง)
const EMPTY_PAYROLL_ADJ = { rows: [] };

// รวม "ยอดยกมา" เข้าคอลัมน์ปลายทางจริง (revenue.*/expenses.*) ของแถวเงินเดือน — ใช้ตอนบันทึก
// เงินเดือน (payload) และตอนพิมพ์ PDF เพื่อให้ยอดสุทธิ/ยอดนำส่งภาษี-ปกส./ยอดต่อคน รวมยอดยกมาด้วย
// (บนหน้าจอโชว์แยกบรรทัดเพื่อเลี่ยงชนกับ useEffect sync ในตาราง — ตอน snapshot ไม่มี sync แล้ว merge ตรงได้)
const CARRYOVER_MERGE_FIELDS = [
  'revenue.salary', 'revenue.overtime', 'revenue.allowances', 'revenue.field_allowance', 'revenue.bonus',
  'expenses.sso', 'expenses.tax', 'expenses.crl', 'expenses.other',
];
function mergeCarryoverRow(row, carry) {
  const byField = carry?.byField;
  if (!byField || Object.keys(byField).length === 0) return row;
  const out = { ...row, revenue: { ...row.revenue }, expenses: { ...row.expenses } };
  CARRYOVER_MERGE_FIELDS.forEach((f) => {
    const add = Number(byField[f]) || 0;
    if (!add) return;
    const [grp, key] = f.split('.');
    out[grp][key] = (parseFloat(out[grp][key]) || 0) + add;
  });
  return out;
}

export default function HrReportV2({
  title, subtitle,
  // ใช้ตอนเปิดเป็น modal จากหน้ารายการเงินเดือน (SalaryListTable) เพื่อดูงวด/เดือนที่ตรงกับ
  // รายการเงินเดือนนั้นๆ ตรวจสอบย้อนหลังว่าตอนบันทึกเงินเดือน พนักงานลงเวลาอะไรยังไง —
  // ถ้าไม่ส่งมา (เปิดจาก route ปกติ) ใช้ค่าเริ่มต้นเป็นเดือน/งวดปัจจุบันเหมือนเดิม
  initialMonth, initialYear, initialTypeSelect, initialSelectedOption,
  // true = ล็อกงวด/เดือนไว้ตายตัว ซ่อนปุ่ม/dropdown เปลี่ยนเดือน-งวด-ประเภทพนักงานทั้งหมด
  // ใช้ตอนเปิดเป็น modal "ตรวจสอบเวลาเข้างาน" — ต้องดูเฉพาะงวดของรายการเงินเดือนที่กดมาเท่านั้น
  lockPeriod = false,
  // ใช้ตอน mount แบบซ่อน (headless) จากปุ่ม "พิมพ์" ในหน้ารายการเงินเดือน — พอข้อมูล/empSummary
  // ของงวดนี้พร้อมแล้ว จะเรียก handlePrintFull() ให้เองอัตโนมัติครั้งเดียว แล้วเรียก onAutoPrintDone
  // กลับไปให้ผู้เรียก unmount instance นี้ทิ้งได้ (ไม่ต้องเปิด modal ให้ user เห็นเลย)
  autoPrintOnReady = false,
  onAutoPrintDone,
  // ใช้ตอน mount แบบซ่อน (headless) เพื่อดึง costResult.rows (ต่อโครงการ) ออกไปทำ PDF เอง โดยไม่ต้องพิมพ์/
  // เปิดหน้าต่างอะไรเลย — เรียกครั้งเดียวตอนข้อมูล/empSummary ของงวดนี้พร้อมแล้ว (เงื่อนไขเดียวกับ autoPrintOnReady)
  onDashboardReady,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  // มือถือ: แถบปุ่ม/ตัวเลือกงวดเรียงแนวตั้ง-ขึ้นบรรทัดใหม่ได้ + ปุ่มใหญ่ขึ้นพอแตะ (คอมเหมือนเดิม)
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const mobileRow = isMobile ? { flexWrap: 'wrap' } : {};
  const mobileTap = isMobile ? { minHeight: 40 } : {};
  const me = useSelector((state) => state.me);

  // ── lockPeriod mode (เปิดจาก "ตรวจสอบเวลาเข้างาน" ใน SalaryListTable.js ผ่าน WindowManager) ──
  // ต้องไม่อ่าน/เขียน redux slice กลาง (state.timestamp/employee/project/ฯลฯ) เพราะ slice พวกนี้
  // มีก้อนเดียวใช้ร่วมกันทั้งแอป ถ้าเปิดหลายหน้าต่างพร้อมกัน (หรือไปหน้าอื่นที่ dispatch ชนิดเดียวกัน)
  // ข้อมูลจะถูกเขียนทับกัน ทำให้หน้าต่างที่ minimize ไว้เห็นข้อมูลผิดงวด/หายไป — โหมดนี้ดึงข้อมูล
  // เก็บใน local state ของ instance ตัวเองแทนทั้งหมด ไม่แตะ dispatch เลย (โหมดปกติ lockPeriod=false
  // ที่หน้า /humen/hr-report-v2 ยังใช้ redux เหมือนเดิมทุกอย่าง ไม่กระทบ)
  const [localEmployees, setLocalEmployees] = useState({ rows: [] });
  const [localTimestamp, setLocalTimestamp] = useState({ rows: [], isCompleted: true });
  const [localTimestampPayroll, setLocalTimestampPayroll] = useState({ rows: [] });
  const [localProject, setLocalProject] = useState({ rows: [] });
  const [localInfo, setLocalInfo] = useState(null);
  // true ต่อเมื่อ fetch "/information/" (กฎมาสาย/attendance_rules) เสร็จแล้วอย่างน้อย 1 ครั้ง (โหมด lockPeriod)
  // ต้องแยกจาก localInfo ตรงๆ เพราะ localInfo อาจเป็น null ได้ทั้งตอน "ยังไม่ fetch" และ "fetch แล้วแต่ไม่มีข้อมูล"
  const [localInfoLoaded, setLocalInfoLoaded] = useState(false);
  const [localManualWork, setLocalManualWork] = useState({ rows: [] });
  const [localLeaveRequest, setLocalLeaveRequest] = useState({ rows: [] });
  const [localSalaryDetail, setLocalSalaryDetail] = useState(null);

  const reduxEmployees = useSelector((state) => state.employee);
  const reduxTimestamp = useSelector((state) => state.timestamp);
  const reduxTimestampPayroll = useSelector((state) => state.timestampPayroll);
  const reduxProject = useSelector((state) => state.project);
  const reduxInfo = useSelector((state) => state.info);
  const reduxManualWork = useSelector((state) => state.manualWork);
  const reduxLeaveRequest = useSelector((state) => state.leaveRequest);
  const reduxPayrollAdj = useSelector((state) => state.payrollAdj);
  const reduxLockedSalaryList = useSelector((state) => state.salaryDetail);

  const employees = lockPeriod ? localEmployees : reduxEmployees;
  const timestamp = lockPeriod ? localTimestamp : reduxTimestamp;
  const timestampPayroll = lockPeriod ? localTimestampPayroll : reduxTimestampPayroll;
  const project = lockPeriod ? localProject : reduxProject;
  const info = lockPeriod ? localInfo : reduxInfo;
  const manualWork = lockPeriod ? localManualWork : reduxManualWork;
  const leaveRequest = lockPeriod ? localLeaveRequest : reduxLeaveRequest;
  // งวดที่ล็อกแล้วใช้ lockedSalaryByEmpId แทน payrollAdj แต่งวดที่ "ยังไม่บันทึกเงินเดือน" ต้องใช้ payrollAdj จริง
  // (ปกส./หักอื่นๆ/ฯลฯ ที่ HR ปรับไว้) ไม่งั้นยอดที่ instance ซ่อน (เช่น ปุ่ม "ดู PDF หลักฐานค่าแรง") จะไม่ตรงกับหน้าจอจริง
  // — เก็บใน local state ของ instance เอง ไม่แตะ redux (เหตุผลเดียวกับตัวแปร local* ด้านบน)
  const [localPayrollAdj, setLocalPayrollAdj] = useState(EMPTY_PAYROLL_ADJ);
  const payrollAdj = lockPeriod ? localPayrollAdj : reduxPayrollAdj;

  const manualRows = useMemo(() => manualWork?.rows || [], [manualWork]);
  const leaveRows = useMemo(() => leaveRequest?.rows || [], [leaveRequest]);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(9999);
  const [dateStart, setDateStart] = useState(dayjs().startOf('month'));
  const [dateEnd, setDateEnd] = useState(dayjs().endOf('month'));
  const [month, setMonth] = useState(initialMonth ?? hrReportLastView?.month ?? dayjs().month());
  const [year, setYear] = useState(initialYear ?? hrReportLastView?.year ?? dayjs().year());
  const [typeSelect, setTypeSelect] = useState(initialTypeSelect ?? hrReportLastView?.typeSelect ?? 'DAILY');
  const [selectedOption, setSelectedOption] = useState(
    initialSelectedOption ?? hrReportLastView?.selectedOption ?? (dayjs().date() <= 15 ? '15' : '31'),
  );
  const [countDay, setCountDay] = useState(
    dayjs(`${year}-${month + 1}`).daysInMonth(),
  );
  const [startDay, setStartDay] = useState(1);

  // รายการเบิก CRL (status = SUCCESS) ที่กดสร้าง (createdAt) อยู่ในช่วง dateStart–dateEnd ของหน้า
  // (มุมพนักงานรายวัน = ครึ่งเดือน, มุมพนักงานรายเดือน = ทั้งเดือน) — ใช้ทำตาราง "สรุปการเบิก CRL"
  // และคอลัมน์ "หัก CRL" ในตารางเงินเดือน (MergedPayrollTableV2) — หักออกจากเงินสุทธิ
  const [crlRows, setCrlRows] = useState([]);
  const [crlLoaded, setCrlLoaded] = useState(false);
  // รวมยอดเบิก CRL ต่อพนักงานในงวดนี้ (employeeId → บาท) — ยัดลง salaryData.${fi}.expenses.crl (read-only)
  const crlByEmpId = useMemo(() => {
    const map = {};
    (crlRows || []).forEach((r) => {
      if (!r.employeeId) return;
      map[String(r.employeeId)] = (map[String(r.employeeId)] || 0) + (Number(r.amount) || 0);
    });
    return map;
  }, [crlRows]);

  // ── ยอดยกไปงวดหน้า (PayrollCarryover) ──────────────────────────────────────
  // incoming = ยอดที่ต้องเอามาลงงวดนี้ (pending, target_period = งวดนี้)
  // outgoing = ยอดที่งวดนี้ยกไปงวดหน้า (auto, source_period = งวดนี้)
  const [carryoverData, setCarryoverData] = useState({ incoming: [], outgoing: [] });
  const [carryoverTick, setCarryoverTick] = useState(0);
  const refetchCarryover = useCallback(() => setCarryoverTick((t) => t + 1), []);
  // โหลดเสร็จแล้วหรือยัง (ใช้รอก่อนส่งยอดออกทาง onDashboardReady กันส่งยอดที่ยังขาด CRL/ยอดยกมา)
  const [carryoverLoaded, setCarryoverLoaded] = useState(false);
  useEffect(() => {
    if (!dateStart || !dateEnd || !typeSelect) return undefined;
    let cancelled = false;
    setCarryoverLoaded(false);
    api
      .get(`${process.env.REACT_APP_API_URL}/payroll-carryover`, {
        params: {
          dateStart: dayjs(dateStart).toISOString(),
          dateEnd: dayjs(dateEnd).toISOString(),
          role_type: typeSelect,
        },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setCarryoverData({
            incoming: Array.isArray(data?.incoming) ? data.incoming : [],
            outgoing: Array.isArray(data?.outgoing) ? data.outgoing : [],
          });
          setCarryoverLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCarryoverData({ incoming: [], outgoing: [] });
          setCarryoverLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [dateStart, dateEnd, typeSelect, carryoverTick]);

  // employeeId → { addTotal, deductTotal, byField, items } — ยอดยกมาลงงวดนี้ (แยกจากตัวเลขในคอลัมน์
  // ตอนโชว์บนจอ, แต่ตอนบันทึก/พิมพ์จะถูก merge เข้าคอลัมน์ปลายทางจริงผ่าน mergeCarryoverRow)
  const carryoverByEmpId = useMemo(() => {
    const map = {};
    (carryoverData.incoming || []).forEach((r) => {
      const id = String(r.employee_id);
      if (!map[id]) map[id] = { addTotal: 0, deductTotal: 0, byField: {}, items: [] };
      const amt = Number(r.amount) || 0;
      if (r.direction === 'add') map[id].addTotal += amt;
      else map[id].deductTotal += amt;
      map[id].byField[r.target_field] = (map[id].byField[r.target_field] || 0) + amt;
      map[id].items.push(r);
    });
    return map;
  }, [carryoverData.incoming]);

  // employeeId → รายการ auto ที่งวดนี้ยกไปงวดหน้า (โชว์ป้าย "ยกไป")
  const carryoverOutByEmpId = useMemo(() => {
    const map = {};
    (carryoverData.outgoing || []).forEach((r) => {
      const id = String(r.employee_id);
      if (!map[id]) map[id] = [];
      map[id].push(r);
    });
    return map;
  }, [carryoverData.outgoing]);

  // วันหยุดของเดือนที่กำลังแสดง — ใช้ให้ PDF ฉบับเต็มทำสีวันหยุด/เสาร์-อาทิตย์ตรงกับ MergedPayrollTableV2
  // (holiday ใช้ซ้ำทุกปี เทียบแค่เดือนตรงกัน ไม่สนใจปี เหมือน MergedPayrollTableV2.js)
  const [holidayDaySet, setHolidayDaySet] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    api
      .get(`${process.env.REACT_APP_API_URL}/holiday?size=1000&page=1`)
      .then(({ data }) => {
        if (cancelled) return;
        const set = new Set(
          (data?.rows || [])
            .filter((h) => dayjs(h.date).month() === month)
            .map((h) => dayjs(h.date).date()),
        );
        setHolidayDaySet(set);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [month]);

  // หมายเหตุรวมทั้งงวด (ไม่ใช่ per-employee) — ไม่มี draft ผูกกับ backend เหมือน note รายคน
  // เลยต้อง reset เองตอนเปลี่ยนงวด/เดือน ไม่งั้นข้อความจากงวดก่อนหน้าจะค้างอยู่ (state เดิมไม่เคยถูกล้าง)
  const [note, setNote] = useState('');
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectEachEmployee, setSelectEachEmployee] = useState(null);

  const { control, getValues, setValue, reset } = useForm();
  // นับรอบ "โหลดข้อมูลงวดใหม่เสร็จสมบูรณ์" (timestampPayroll + payrollAdj อัปเดตครบแล้ว)
  // ใช้ trigger reset ฟอร์ม react-hook-form — field ผูกด้วย index (salaryData.{fi}.xxx) ไม่ได้ผูกกับ
  // employee_id/งวดโดยตรง ถ้าไม่ reset ค่าที่เคยพิมพ์ไว้ในงวด/เดือนก่อนหน้าจะค้างอยู่ที่ index เดิม
  // แล้วโผล่มาใช้ผิดที่ตอนสลับไปงวด/เดือนอื่น (defaultValue ของ Controller ใช้แค่ตอน register ครั้งแรกเท่านั้น)
  const [periodLoadTick, setPeriodLoadTick] = useState(0);
  const formResetTickRef = useRef(-1);

  const [salaryData, setSalaryData] = useState([]);

  // เลขค่าแรง/OT/ค่าออกสนามแยกตามโครงการ — คำนวณจาก MergedPayrollTableV2 เอง (สูตรเดียวกับตารางเป๊ะๆ)
  // ส่งต่อให้ TimestampProjectCost แทนการพึ่ง timestamp.dashboard จาก backend (คนละ loop คำนวณ ทำให้เคยไม่ตรงกัน)
  const [projectDashboard, setProjectDashboard] = useState([]);
  // สรุปวันทำงาน/OT ชม. ต่อคน (empSummaryMap) จาก MergedPayrollTableV2 — ใช้ตอนสร้าง PDF สรุปทั้งหน้าให้ตรงกับตารางเป๊ะๆ
  const [empSummary, setEmpSummary] = useState({});

  // ยอดต้นทุนต่อโครงการ (รวม total_payable "ที่ต้องจ่าย") — สูตรเดียวกับ TimestampProjectCost ที่โชว์บนจอ
  // ยกมา hoist ไว้ตรงนี้ที่เดียว ใช้ใน payload ตอนกดบันทึกเงินเดือน (onSubmit) — ตัวเลขชุดเดียวกันเป๊ะ ไม่คำนวณซ้ำ
  const costResult = useMemo(
    // computeProjectCostRows รับแค่ baseRows — total_sso คำนวณจาก employee_deductions ของแต่ละแถวเองแล้ว
    // (ดู projectCostFormula.js) totalSsoAmount ไม่ได้ใช้ในการคำนวณค่าแรงต่อโครงการอีกต่อไป
    () => computeProjectCostRows(projectDashboard),
    [projectDashboard],
  );

  const daysInMonth = dayjs(`${year}-${month + 1}`).daysInMonth();
  const days = useMemo(() => Array.from({ length: countDay }, (_, i) => i + startDay), [countDay, startDay]);
  const attendanceRules = useMemo(() => info?.setting?.payroll?.attendance_rules || [], [info]);
  // true ต่อเมื่อโหลดกฎมาสาย (attendance_rules) เสร็จแล้วจริง — ต่างจากแค่เช็ค attendanceRules.length
  // เพราะบริษัทที่ไม่ตั้งกฎมาสายเลยก็ต้องได้ [] เหมือนกัน ต้องแยกให้ออกจากสถานะ "ยังโหลดไม่เสร็จ"
  // ใช้กันตารางคำนวณหักสาย (MergedPayrollTableV2) รันด้วย attendanceRules=[] ชั่วคราวตอนหน้าเว็บเพิ่งโหลด
  // (ก่อน getInformation() เสร็จ) แล้วได้ค่าหักสายผิด (ไม่มี threshold ผ่อนผัน) ก่อนจะเด้งเป็นค่าถูกทีหลัง
  const attendanceRulesLoaded = lockPeriod ? localInfoLoaded : !!reduxInfo?.isCompleted;

  // ── ตรวจว่างวดนี้ (dateStart/dateEnd/typeSelect) เคย "บันทึกเงินเดือน" ไปแล้วหรือยัง ──
  // ถ้าเคยแล้ว → ล็อกตารางเป็น read-only ป้องกันบันทึกซ้ำ + ดึงยอดที่บันทึกจริงจาก salaries มาแสดงแทนค่าคำนวณสด
  const [isPeriodLocked, setIsPeriodLocked] = useState(false);
  // true เมื่อเช็คแล้วว่างวดนี้ล็อกหรือไม่ (และโหลด snapshot ของงวดที่ล็อกมาแล้วในโหมด lockPeriod) —
  // ใช้รอก่อนส่งยอดออกทาง onDashboardReady กันส่งยอดสดไปก่อนที่จะรู้ว่าต้องใช้ snapshot
  const [periodChecked, setPeriodChecked] = useState(false);
  // lockPeriod (เปิดจากปุ่ม "ตรวจสอบเวลาเข้างาน" ใน SalaryListTable.js) ต้อง read-only เสมอ
  // ไม่พึ่งแค่ isPeriodLocked (ผลเช็คจาก backend) เพราะ dateStart/dateEnd ที่คำนวณจาก initialMonth/initialYear
  // อาจไม่ match กับที่บันทึกไว้เป๊ะ ทำให้เช็คไม่เจอและยังแก้ไขได้อยู่ทั้งที่ตั้งใจเปิดมาดูอย่างเดียว
  const effectiveReadOnly = isPeriodLocked || lockPeriod;

  // รายการเบิกค่าแรง (Expenses isLabor) — sync สดเข้า Expenses (status PENDING) + Budget.pending_items ระหว่าง
  // ยังไม่กดบันทึกเงินเดือน (debounce กันยิง API ทุกครั้งที่ตัวเลขขยับ) และ sync ครั้งสุดท้ายอีกทีตอนกดบันทึก
  // จริง (submitLaborDraftsToPending ใน salaryList.service.js — ยอดสุดท้ายตอนปิดงวด + เก็บกวาดโครงการที่หลุด)
  // — sync เฉพาะตอนมีการแก้ไขค่าจริงอย่างน้อย 1 ครั้งในงวดนี้แล้วเท่านั้น (ดู editedTick ด้านล่าง) ไม่ sync แค่
  // เพราะเปิดหน้าเข้ามาดูเฉยๆ — ข้าม lockPeriod (modal/print instance แยก ไม่ใช่การแก้จริง) และงวดที่ล็อก/
  // บันทึกไปแล้ว (effectiveReadOnly) กันสร้าง draft ย้อนหลังให้งวดปิดแล้ว
  //
  // นับว่า "มีการแก้ไขค่าจริง" ในงวดนี้ไปแล้วหรือยัง (state ไม่ใช่ ref — ต้องการให้ effect debounce sync ด้านล่าง
  // trigger ต่อจากค่านี้เท่านั้น ไม่ใช่ต่อ costResult ตรงๆ ดูเหตุผลที่ effect นั้น) รีเซ็ตเป็น 0 ทุกครั้งที่เปลี่ยน
  // งวด แก้ปัญหา "แค่เปิดหน้าเข้ามาดูงวดที่ยังไม่บันทึก ก็ auto sync สร้างรายการเบิกให้ทันทีทั้งที่ยังไม่ได้แก้อะไรเลย"
  const [editedTick, setEditedTick] = useState(0);
  const markEdited = useCallback(() => setEditedTick((v) => v + 1), []);
  useEffect(() => { setEditedTick(0); }, [dateStart, dateEnd, typeSelect]);

  // costResult เปลี่ยน reference ทุกครั้งที่พิมพ์ในช่องไหนก็ได้ของฟอร์มเงินเดือน (watchedSalaryData จาก
  // useWatch({name:'salaryData'}) watch ทั้งก้อน array พนักงานทั้งหมด ไม่ใช่แค่ช่องที่พิมพ์ — ดู
  // MergedPayrollTableV2.js) เก็บไว้ใน ref แยกไม่ให้ effect debounce ด้านล่างต้อง depend on มันตรงๆ
  const costResultRef = useRef(costResult);
  costResultRef.current = costResult;

  // sync ยอดค่าแรงต่อโครงการเข้า Expenses (isLabor, status PENDING) ระหว่างยังไม่กดบันทึกเงินเดือน —
  //
  // **บั๊กที่แก้ไปแล้ว (ต้องคงไว้แบบนี้):** เดิม effect นี้เคยผูก dependency กับ costResult ตรงๆ — เพราะ
  // costResult เปลี่ยน reference ทุก keystroke ของฟอร์มเงินเดือนทั้งก้อน (ไม่ใช่แค่ช่องที่พิมพ์ ดูคอมเมนต์
  // costResultRef ด้านบน) ทำให้ clearTimeout+ตั้งใหม่รัวๆ ทุกครั้งที่พิมพ์อะไรก็ได้ ถ้า user พิมพ์ต่อเนื่องไม่เว้น
  // จังหวะเกิน 2.5 วิ (เกิดขึ้นปกติเวลากรอกหลายคน) ตัวจับเวลาจะไม่มีวันครบเวลาเลย จน sync ไม่ถูก trigger เลย
  // จนกว่าจะหยุดพิมพ์นิ่งจริงๆ — แก้โดยผูก effect กับ editedTick (นับเฉพาะตอน markEdited() ถูกเรียกจริงจาก
  // action ที่ dispatch จริงเท่านั้น) แทน แล้วอ่านยอดสดสุดจาก costResultRef ตอนตัวจับเวลาครบเวลาจริงๆ
  const laborSyncTimerRef = useRef(null);
  // เก็บ "ตัว sync ที่ค้างอยู่ตอนนี้" ไว้เรียก flush ทันทีจาก effect แยกด้านล่าง (ตอนออกจากงวดนี้จริงๆ) —
  // null แปลว่าไม่มี sync ค้างอยู่ ณ ขณะนี้
  const flushLaborSyncRef = useRef(null);
  useEffect(() => {
    if (lockPeriod || effectiveReadOnly || !dateStart || !dateEnd) { flushLaborSyncRef.current = null; return undefined; }
    if (editedTick === 0) { flushLaborSyncRef.current = null; return undefined; }
    // กันบั๊กสลับแท็บรายวัน/รายเดือน: typeSelect/selectedOption เปลี่ยนทันทีใน render เดียวกัน แต่ dateStart/dateEnd
    // ถูก set ตามมาใน effect (render ถัดไป) — render คั่นกลางนั้น typeSelect เป็นประเภทใหม่แต่ช่วงวันที่ + costResult
    // ยังเป็นของประเภทเดิม ถ้าตั้ง doSync ไว้ตอนนั้น effect flush ด้านล่าง (ผูก dateStart) จะยิงมันทันทีในรอบถัดไป
    // → เกิดรายการเบิก "รายวัน งวดที่1. 1-30" ที่ยอดเป็นของรายเดือน (และกลับกัน) — เจอจริง 28/09/2569
    // ตั้ง sync เฉพาะตอนช่วงวันที่ตรงกับงวดของประเภท/ตัวเลือกปัจจุบันเท่านั้น
    const expected = computeRange(year, month, selectedOption);
    if (expected.start !== dateStart || expected.end !== dateEnd) { flushLaborSyncRef.current = null; return undefined; }
    const doSync = () => {
      laborSyncTimerRef.current = null;
      flushLaborSyncRef.current = null;
      const rows = (costResultRef.current.rows || [])
        .filter((r) => r.project_id)
        .map((r) => ({
          project_id: r.project_id,
          project_number: r.project_number,
          project_name: r.project_name,
          amount: r.total_payable || 0,
          // ยอดรายคนของโครงการนี้ — backend ใช้แยกรายการเบิกของพนักงานที่ติ๊ก "ไม่จ่ายเงินตามโครงการ" ออกเป็นอีกใบ
          employee_payables: r.employee_payables || [],
        }));
      if (rows.length === 0) return;
      // ส่ง dateStart/dateEnd แบบดิบเหมือน onSubmit เป๊ะ (ห้าม format('YYYY-MM-DD') ก่อนส่ง) — reformat ผ่าน
      // dayjs ในเบราว์เซอร์จะตีความ timezone ต่างจากตอน onSubmit ส่งค่าดิบ ทำให้ periodStart/periodEnd
      // ที่ backend คำนวณจากสอง call นี้คลาดกันได้ (ดู expenses.service.js syncLaborDraft/submitLaborDraftsToPending)
      Promise.resolve(dispatch(actions.laborDraftSync({
        dateStart,
        dateEnd,
        typeSelect,
        rows,
      }))).finally(() => setLaborStatusTick((t) => t + 1));
    };
    clearTimeout(laborSyncTimerRef.current);
    laborSyncTimerRef.current = setTimeout(doSync, 2500);
    flushLaborSyncRef.current = () => { clearTimeout(laborSyncTimerRef.current); doSync(); };
    return () => clearTimeout(laborSyncTimerRef.current);
  }, [editedTick, dateStart, dateEnd, typeSelect, year, month, selectedOption, lockPeriod, effectiveReadOnly, dispatch]);

  // เปลี่ยนงวด/ประเภทพนักงาน หรือปิด/ออกจากหน้านี้ไปเลย (unmount) — flush ตัว sync ที่ยังค้างตัวจับเวลาอยู่
  // ทันที (ถ้ามี) ก่อนออก แทนการโดน cancel เงียบๆ ทิ้งการแก้ไขล่าสุดไปโดยไม่มีใครรู้ตัว ต้องแยก effect ต่างหาก
  // จาก effect ด้านบน เพราะ effect ด้านบน cleanup ทุกครั้งที่ editedTick ขยับด้วย (ซึ่งคือ "แก้ต่อ" ปกติ ไม่ใช่
  // "ออกจากงวดนี้") ถ้า flush ในนั้นเลยจะกลายเป็นยิง sync ทันทีทุกครั้งที่แก้ค่า เสีย debounce ไปทั้งหมด — effect
  // นี้ผูกกับ [dateStart, dateEnd, typeSelect] เท่านั้น cleanup จะรันแค่ตอนเปลี่ยนงวด/ประเภทพนักงานจริง หรือ
  // component unmount (ปิดหน้า/navigate ออก) เท่านั้น
  useEffect(() => () => flushLaborSyncRef.current?.(), [dateStart, dateEnd, typeSelect]);

  // ยอดที่บันทึกจริงของงวดนี้ (จาก salaries collection ผ่าน salaryDetailGet) — key: employee_id
  const [lockedSalaryByEmpId, setLockedSalaryByEmpId] = useState(null);
  // เปิด/ปิด modal "รายละเอียดเงินเดือน" (แทนการเด้งไปหน้า /humen/salarylist/detail/:id)
  const [showSalaryDetailModal, setShowSalaryDetailModal] = useState(false);
  const [showOtSummaryModal, setShowOtSummaryModal] = useState(false);
  const [showFormulaExplain, setShowFormulaExplain] = useState(false);
  // โครงการที่กดปุ่มสูตรในแถวตารางต้นทุน ({ key, external, number }) — null = ดูทุกคนทุกโครงการ
  const [explainFocus, setExplainFocus] = useState(null);
  const [showCrlSummaryModal, setShowCrlSummaryModal] = useState(false);
  // { employee_id, name } ของแถวที่กดเปิด dialog "ยอดยกไปงวดหน้า" — null = ปิด
  const [carryoverEmp, setCarryoverEmp] = useState(null);
  const handleOpenCarryover = useCallback((emp) => {
    setCarryoverEmp(emp ? { employee_id: String(emp.employee_id), name: emp.name } : null);
  }, []);
  // เด้งถามตอนบันทึกเมื่อมีพนักงานเงินเดือนติดลบ — promise-based (resolve เมื่อ HR ตัดสินใจ)
  const [carryoverPrompt, setCarryoverPrompt] = useState(null); // { list, resolve } | null
  const askCarryoverOptIn = useCallback(
    (list) => new Promise((resolve) => setCarryoverPrompt({ list, resolve })),
    [],
  );
  useEffect(() => {
    let cancelled = false;
    setPeriodChecked(false);
    if (!dateStart || !dateEnd) {
      setIsPeriodLocked(false);
      setLockedSalaryByEmpId(null);
      return undefined;
    }
    dispatch(actions.salaryListCheckPeriod({ dateStart, dateEnd, role_type: typeSelect }))
      .then((res) => {
        if (cancelled) return;
        setIsPeriodLocked(!!res?.exists);
        if (res?.exists && res?.id) {
          if (lockPeriod) {
            api.get(`${process.env.REACT_APP_API_URL}/salary-list/${res.id}`)
              .then(({ data }) => { if (!cancelled) setLocalSalaryDetail(data); })
              .catch(() => {})
              .finally(() => { if (!cancelled) setPeriodChecked(true); });
          } else {
            dispatch(actions.salaryDetailGet(res.id)).catch(() => {});
            setPeriodChecked(true);
          }
        } else {
          setLockedSalaryByEmpId(null);
          setPeriodChecked(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsPeriodLocked(false);
          setLockedSalaryByEmpId(null);
          setPeriodChecked(true);
        }
      });
    return () => { cancelled = true; };
  }, [dispatch, dateStart, dateEnd, typeSelect, lockPeriod]);

  // โหมดคำนวณค่าออกสนามของงวดนี้ ("new" ค่าเริ่มต้นเสมอ) — งวดที่ยังไม่บันทึกเงินเดือน
  // (isPeriodLocked=false) มีผลจริงกับยอดที่จะบันทึก ใช้ดูงวดเก่าที่ค้างจ่าย (ก่อนตัด auto-detect
  // จากรหัสโครงการออก) ส่วนงวดที่ locked แล้วโหมดนี้แก้ยอดจริงไม่ได้อีก (ดู handleFaCalcModeChange)
  // แต่ยัง toggle ดูเปรียบเทียบได้ — ใช้เช็คว่ายอด "ค่าออกสนาม" ที่คำนวณสดตามโหมดนี้ตรงกับยอดที่บันทึก
  // จริงไหม (ไฮไลต์แดงถ้าไม่ตรง ดู MergedPayrollTableV2.js คอลัมน์ field_allowance)
  const [faCalcMode, setFaCalcMode] = useState('new');
  useEffect(() => {
    if (!dateStart || !dateEnd || isPeriodLocked) {
      setFaCalcMode('new');
      return undefined;
    }
    let cancelled = false;
    dispatch(actions.fieldAllowanceCalcModeGet({ dateStart, dateEnd, role_type: typeSelect }))
      .then((res) => { if (!cancelled) setFaCalcMode(res?.mode === 'old' ? 'old' : 'new'); })
      .catch(() => { if (!cancelled) setFaCalcMode('new'); });
    return () => { cancelled = true; };
  }, [dispatch, dateStart, dateEnd, typeSelect, isPeriodLocked]);

  // งวด locked แก้โหมดจริงไม่ได้แล้ว — toggle ตรงนี้แค่เปลี่ยน state ฝั่ง client เพื่อดูเปรียบเทียบ
  // ยอดคำนวณสดกับยอดที่บันทึกจริง ไม่ยิง fieldAllowanceCalcModeSet ไปบันทึกซ้ำ
  const handleFaCalcModeChange = useCallback((nextMode) => {
    setFaCalcMode(nextMode);
    if (!dateStart || !dateEnd || isPeriodLocked) return;
    dispatch(actions.fieldAllowanceCalcModeSet({
      dateStart, dateEnd, role_type: typeSelect, mode: nextMode,
    })).catch(() => {});
  }, [dispatch, dateStart, dateEnd, typeSelect, isPeriodLocked]);

  // salaryDetail (state.salaryDetail) อัปเดตหลัง salaryDetailGet ด้านบนสำเร็จ — สร้าง lookup map ตาม employee_id
  // ใช้ slice แยกจาก state.salaryList (rows ของหน้ารายการ) โดยเจตนา — ไม่งั้นตอนเปิดหน้านี้เป็น
  // modal จากหน้ารายการเงินเดือน (HrSalaryList) การ fetch รายละเอียด 1 งวดจะไปทับ rows ของตารางหลัง modal
  // lockPeriod ใช้ localSalaryDetail แทน (ไม่งั้นเปิดหลายหน้าต่างพร้อมกันจะทับ state.salaryDetail กันเอง)
  const lockedSalaryList = lockPeriod ? localSalaryDetail : reduxLockedSalaryList;

  // แสดงผลใน <TimestampProjectCost> ด้านล่าง: งวด "ล็อกแล้ว" ให้ใช้ snapshot ที่บันทึกจริงตอนกดบันทึกเงินเดือน
  // (lockedSalaryList.dashboard, มาจาก salaries collection) แทน projectDashboard ที่คำนวณสด — กันตัวเลข ปกส./
  // ต้นทุนต่อโครงการขยับตาม ถ้ามีคนแก้ checkin/ใบลา/manual work ของงวดที่ปิดบัญชีไปแล้วในภายหลัง (ก่อนหน้านี้ตั้งใจ
  // ให้คำนวณสดเสมอเพื่อเลี่ยง snapshot งวดเก่าที่บันทึกผิดพลาดก่อนแก้บั๊ก OT — เปลี่ยนตามที่ user ยืนยันว่าต้องการ
  // ให้ตรงกับตอนกดบันทึกเป๊ะแทน แม้งวดเก่ามากๆที่ไม่มี snapshot เก็บ ปกส.ต่อคน (employee_deductions) ไว้จะโชว์ 0 ก็ตาม)
  // งวดที่บันทึกก่อนมี field `dashboard` เลย (เก่ามากๆ) จะได้ array ว่าง — fallback ไปคำนวณสดกันตารางว่างเปล่าไปเลย
  // costResult (payload ตอนบันทึกเงินเดือน) ยังอิง projectDashboard สดเสมอตามเดิม — ต้องการยอดตามสูตรปัจจุบัน
  const displayProjectDashboard = (isPeriodLocked && Array.isArray(lockedSalaryList?.dashboard) && lockedSalaryList.dashboard.length > 0)
    ? lockedSalaryList.dashboard
    : projectDashboard;

  useEffect(() => {
    if (!isPeriodLocked || !lockedSalaryList?.salaryData) return;
    const byEmpId = {};
    lockedSalaryList.salaryData.forEach((s) => {
      byEmpId[String(s.employee)] = s;
    });
    setLockedSalaryByEmpId(byEmpId);
  }, [isPeriodLocked, lockedSalaryList]);

  // เคลียร์หมายเหตุรวมทันทีที่เปลี่ยนงวด/เดือน — กันข้อความจากงวดก่อนหน้าค้างมาโผล่ในงวดใหม่
  useEffect(() => {
    setNote('');
  }, [dateStart, dateEnd, typeSelect]);

  // โหลดรายการเบิก CRL (SUCCESS) ที่กดสร้าง (createdAt) อยู่ในช่วง dateStart–dateEnd ของหน้า —
  // ช่วงนี้เข้ารหัสมุมที่ดูอยู่แล้ว (พนักงานรายวัน = ครึ่งเดือน, พนักงานรายเดือน = ทั้งเดือน)
  useEffect(() => {
    if (!dateStart || !dateEnd) return undefined;
    let cancelled = false;
    setCrlLoaded(false);
    api
      .get(`${process.env.REACT_APP_API_URL}/expenses/crl-summary`, {
        params: {
          dateStart: dayjs(dateStart).toISOString(),
          dateEnd: dayjs(dateEnd).toISOString(),
        },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setCrlRows(Array.isArray(data) ? data : []);
          setCrlLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCrlRows([]);
          setCrlLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dateStart, dateEnd]);

  // สถานะรายการเบิกค่าแรงที่สร้างอัตโนมัติของงวดนี้ (แสดงในคอลัมน์ "สถานะรายการเบิก" ของ TimestampProjectCost) —
  // โหลดใหม่เมื่อเปลี่ยนงวด/ประเภท, หลัง sync/บันทึกเงินเดือน (laborStatusTick) และทุก 60 วินาที
  // (สถานะเปลี่ยนจากการอนุมัติ/จ่ายเงินที่หน้าอื่น)
  const [laborStatuses, setLaborStatuses] = useState([]);
  const [laborStatusTick, setLaborStatusTick] = useState(0);
  useEffect(() => {
    if (!dateStart || !dateEnd) return undefined;
    let cancelled = false;
    const load = () => api
      .get(`${process.env.REACT_APP_API_URL}/expenses/labor-status`, {
        params: { dateStart: dayjs(dateStart).toISOString(), dateEnd: dayjs(dateEnd).toISOString(), typeSelect },
      })
      .then(({ data }) => { if (!cancelled) setLaborStatuses(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setLaborStatuses([]); });
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [dateStart, dateEnd, typeSelect, laborStatusTick]);

  // ถ้างวดนี้บันทึกเงินเดือนไปแล้ว ดึงหมายเหตุที่เคยบันทึกจริงมาแสดงแทน (ช่องนี้เป็น read-only ตอน locked อยู่แล้ว)
  useEffect(() => {
    if (isPeriodLocked && lockedSalaryList?.note != null) {
      setNote(lockedSalaryList.note);
    }
  }, [isPeriodLocked, lockedSalaryList]);

  useEffect(() => {
    const adjByEmpId = {};
    (payrollAdj?.rows || []).forEach((a) => { adjByEmpId[String(a.employee_id)] = a; });
    const format2 = (val) => val != null && val !== '' ? Number(val).toFixed(2) : val;

    // งวด locked (บันทึกเงินเดือนไปแล้ว) → ใช้ยอดที่บันทึกจริงจาก salaries แทนค่าคำนวณสด/payrollAdj
    // (grid วันที่/checkins ยังใช้ timestampPayroll เดิม เพื่อให้ดูรายละเอียดรายวันได้ตามปกติ)
    const newData = (timestampPayroll?.rows || []).map((row) => {
      const locked = isPeriodLocked
        ? lockedSalaryByEmpId?.[String(row.employee_id)]
        : null;
      if (locked) {
        return {
          ...row,
          manual_fields: [],
          totalWork: locked.totalWork ?? row.totalWork,
          revenue: {
            ...row.revenue,
            salary: format2(locked.revenue?.salary),
            allowances: format2(locked.revenue?.allowances),
            bonus: format2(locked.revenue?.bonus),
            overtime: format2(locked.revenue?.overtime),
            field_allowance: format2(locked.revenue?.field_allowance),
          },
          expenses: {
            ...row.expenses,
            tax: format2(locked.expenses?.tax),
            other: format2(locked.expenses?.other),
            sso: format2(locked.expenses?.sso),
            late: format2(locked.expenses?.late),
            // งวด locked → ใช้ยอด CRL ที่บันทึกจริง (fallback ยอดสดถ้า snapshot เก่าไม่มี field นี้)
            crl: format2(locked.expenses?.crl ?? crlByEmpId[String(row.employee_id)] ?? 0),
          },
          total: locked.total,
          // งวด locked → ยอดยกมาถูก apply ไปแล้ว (อยู่ใน locked.total/snapshot) ไม่นับซ้ำ
          // (carryoverByEmpId มีแต่ entry ที่ status='pending' อยู่แล้ว งวด locked จะ = ว่างเสมอ)
          carryover: carryoverByEmpId[String(row.employee_id)] || { addTotal: 0, deductTotal: 0, items: [] },
          // ต้องเป็น string เสมอ ห้ามเป็น undefined — Controller เป็น controlled input
          // ถ้า reset() ส่งค่า undefined เข้าไป React จะไม่เคลียร์ข้อความเดิมที่ค้างอยู่ใน DOM
          // (ไปงวดที่ไม่มีหมายเหตุ แต่ input ยังโชว์ข้อความของงวดก่อนหน้าค้างอยู่)
          note: locked.note ?? row.note ?? '',
        };
      }
      const adj = adjByEmpId[String(row.employee_id)] || {};
      const mf = adj.manual_fields || [];
      return {
        ...row,
        manual_fields: mf,
        revenue: {
          ...row.revenue,
          salary: format2(mf.includes('salary') ? (adj.revenue?.salary ?? row.revenue?.salary) : row.revenue?.salary),
          allowances: format2(mf.includes('allowances') ? (adj.revenue?.allowances ?? row.revenue?.allowances) : row.revenue?.allowances),
          bonus: format2(mf.includes('bonus') ? (adj.revenue?.bonus ?? row.revenue?.bonus) : row.revenue?.bonus),
          overtime: format2(mf.includes('overtime') ? (adj.revenue?.overtime ?? row.revenue?.overtime) : row.revenue?.overtime),
          field_allowance: format2(mf.includes('field_allowance') ? (adj.revenue?.field_allowance ?? row.revenue?.field_allowance) : row.revenue?.field_allowance),
        },
        expenses: {
          ...row.expenses,
          tax: format2(mf.includes('tax') ? (adj.expenses?.tax ?? row.expenses?.tax) : row.expenses?.tax),
          other: format2(mf.includes('other') ? (adj.expenses?.other ?? row.expenses?.other) : row.expenses?.other),
          sso: format2(mf.includes('sso') ? (adj.expenses?.sso ?? row.expenses?.sso) : row.expenses?.sso),
          late: format2(mf.includes('late') ? (adj.expenses?.late ?? row.expenses?.late) : row.expenses?.late),
          // หัก CRL — คำนวณอัตโนมัติจากรายการเบิก CRL (crl-summary) แก้มือไม่ได้ ไม่ผ่าน payrollAdj
          crl: format2(crlByEmpId[String(row.employee_id)] || 0),
        },
        // ยอดยกมาลงงวดนี้ (แยกจากตัวเลขในคอลัมน์ — บวก/ลบสุทธิเข้ายอด "ที่ต้องจ่าย"/สุทธิ)
        carryover: carryoverByEmpId[String(row.employee_id)] || { addTotal: 0, deductTotal: 0, items: [] },
        // เหตุผลเดียวกับ branch locked ด้านบน — ห้ามปล่อยเป็น undefined
        note: adj.note ?? row.note ?? '',
      };
    });
    setSalaryData(newData);

    // reset ฟอร์มพร้อมค่าตั้งต้นของงวดนี้ครั้งเดียวต่อรอบโหลด (periodLoadTick เพิ่มหลัง
    // timestampPayroll + payrollAdj ของงวดใหม่มาครบแล้วเท่านั้น — ดู refreshAll) กัน field
    // ที่ผูกด้วย index ค้างค่าจากงวด/เดือนก่อนหน้า (โดยเฉพาะ tax/other/note ที่ไม่มี useEffect
    // sync คอยเซ็ตค่าซ้ำอยู่แล้วแบบ salary/sso/allowances/field_allowance)
    if (formResetTickRef.current !== periodLoadTick) {
      formResetTickRef.current = periodLoadTick;
      reset({ salaryData: newData });
    }
  }, [timestampPayroll, payrollAdj, isPeriodLocked, lockedSalaryByEmpId, periodLoadTick, crlByEmpId, carryoverByEmpId]); // eslint-disable-line

  const [isDataLoading, setIsDataLoading] = useState(false);

  const refreshAll = useCallback(async (start, end, type, forceRefresh = false) => {
    setIsDataLoading(true);
    try {
      if (lockPeriod) {
        // fetch ตรงเก็บ local state แทน dispatch — endpoint/query เดียวกับ actions ที่ใช้ตอนไม่ lock
        // (ไม่ผ่าน cache ด้านล่างเลย — โหมดนี้ต้องได้ข้อมูลสดเสมอ + แยก instance ไม่ให้ปนกัน)
        const [dashboardRes, payrollRes, manualRes, leaveRes, adjRes] = await Promise.all([
          api.get(`${process.env.REACT_APP_API_URL}/timestamp/timestamp-payroll-dashboard?dateStart=${start}&dateEnd=${end}&typeSelect=${type}`),
          api.get(`${process.env.REACT_APP_API_URL}/timestamp/timestamp-payroll?dateStart=${start}&dateEnd=${end}&typeSelect=${type}`),
          api.get(`${process.env.REACT_APP_API_URL}/manual-work-entry`, { params: { dateStart: start, dateEnd: end } }),
          api.get(`${process.env.REACT_APP_API_URL}/leave-request?me=&status=APPROVED&size=999999&page=1&dateStart=${start}&dateEnd=${end}`),
          api.get(`${process.env.REACT_APP_API_URL}/payroll-adjustment`, { params: { dateStart: start, dateEnd: end } })
            .catch(() => ({ data: EMPTY_PAYROLL_ADJ })),
        ]);
        setLocalPayrollAdj(adjRes.data || EMPTY_PAYROLL_ADJ);
        setLocalTimestamp({ ...dashboardRes.data, isCompleted: true });
        setLocalTimestampPayroll(payrollRes.data);
        setLocalManualWork(manualRes.data);
        setLocalLeaveRequest(leaveRes.data);
      } else {
        const cacheKey = `${type}|${start}|${end}`;
        const cached = hrReportDataCache.get(cacheKey);
        const isFresh = cached && Date.now() - cached.cachedAt < HR_REPORT_CACHE_TTL;

        if (isFresh && !forceRefresh) {
          // cache hit — ยัดข้อมูลที่เคยโหลดกลับเข้า Redux slice เดิม ไม่ยิง API ซ้ำ
          dispatch({ type: TIMESTAMP_ALL, payload: cached.data.dashboard });
          dispatch({ type: TIMESTAMP_PAYROLL_ALL, payload: cached.data.payroll });
          dispatch({ type: MANUAL_WORK_ALL, payload: cached.data.manualWork });
          dispatch({ type: LEAVEREQUEST_ALL, payload: cached.data.leaveRequest });
          dispatch({ type: PAYROLL_ADJ_ALL, payload: cached.data.payrollAdj });
        } else {
          const [both, manualWork, leaveRequest, payrollAdj] = await Promise.all([
            dispatch(actions.timestampPayrollFetchBoth({ dateStart: start, dateEnd: end, typeSelect: type })),
            dispatch(actions.manualWorkAll({ dateStart: start, dateEnd: end })),
            dispatch(actions.leaveRequestAll({ status: 'APPROVED', dateStart: start, dateEnd: end })),
            dispatch(actions.payrollAdjAll({ dateStart: start, dateEnd: end })),
          ]);
          hrReportDataCache.set(cacheKey, {
            cachedAt: Date.now(),
            data: { dashboard: both?.dashboard, payroll: both?.payroll, manualWork, leaveRequest, payrollAdj },
          });
        }
      }
      // ทั้ง timestampPayroll และ payrollAdj ของงวดนี้มาครบแล้ว ณ จุดนี้ — ปลอดภัยที่จะ reset ฟอร์ม
      setPeriodLoadTick((t) => t + 1);
    } finally {
      setIsDataLoading(false);
    }
  }, [dispatch, lockPeriod]);

  const fetchTimerRef = useRef(null);

  // คำนวณช่วงวันที่ (start/end/countDay/startDay) จาก (ปี, เดือน, ตัวเลือกงวด) — เป็น pure function
  // แยกออกมาเพื่อใช้ซ้ำทั้ง effect หลัก และตอนเช็ค boundary ก่อนเลื่อนเดือน/งวด (handlePreviousMonth/handleNextMonth)
  // ห้ามใช้ .format('YYYY-MM-DDT00:00:00.000[Z]') แบบเดิม — [Z] ตรงนั้นเป็นตัวอักษรที่แปะต่อท้ายเฉยๆ ไม่ได้แปลง
  // เวลาไทยเป็น UTC จริง .toISOString() คือค่าที่ถูกต้องแทน
  //
  // เดิม dayjs() เปล่าๆ อิง timezone ของเครื่อง/เบราว์เซอร์ที่รันโค้ดนี้ตรงๆ (สมมติว่าเป็นเวลาไทย) — ถ้าเครื่อง
  // ที่รันตั้ง timezone ไม่ตรงเวลาไทยจริง (เช่น เครื่อง server/browser ที่ไปตั้ง UTC ไว้) ขอบเขตงวดที่คำนวณได้จะ
  // คลาดไปสูงสุด 7 ชม. เช็คอินช่วงเที่ยงคืนตรงขอบงวด (00:00-07:00 น. ของวันที่ 1/16, หรือเช้าวันถัดจากวันสุดท้าย
  // ของงวด) จะถูกนับผิดงวด — พบจริงว่าทำให้ยอด total_payable ที่คำนวณจากคนละเครื่อง (เช่น ปุ่ม "รีเซ็ต" ใน
  // ExpensesTabLabor.js ที่ mount HrReportV2 headless บนเครื่องนึง เทียบกับหน้านี้ที่ user เปิดเองปกติบนอีก
  // เครื่อง) ไม่ตรงกัน — บั๊กคลาสเดียวกับที่เพิ่งแก้ฝั่ง backend ไปแล้วใน timestamp.service.js (SSO cross-period
  // check) แก้โดยผูก .tz(THAI_TZ) ตายตัวแทนการพึ่ง timezone ของเครื่องที่รัน
  const computeRange = (y, m, opt) => {
    let start; let end; let countDay; let startDay;
    if (opt === 'Month') {
      start = dayjs()
        .tz(THAI_TZ).year(y).month(m)
        .startOf('month')
        .toISOString();
      end = dayjs()
        .tz(THAI_TZ).year(y).month(m)
        .endOf('month')
        .toISOString();
      countDay = dayjs(`${y}-${m + 1}`).daysInMonth();
      startDay = 1;
    } else if (opt === '15') {
      start = dayjs()
        .tz(THAI_TZ).year(y).month(m)
        .startOf('month')
        .toISOString();
      end = dayjs()
        .tz(THAI_TZ).year(y).month(m)
        .date(15)
        .endOf('day')
        .toISOString();
      countDay = 15;
      startDay = 1;
    } else if (opt === '31') {
      start = dayjs()
        .tz(THAI_TZ).year(y).month(m)
        .date(16)
        .startOf('day')
        .toISOString();
      end = dayjs()
        .tz(THAI_TZ).year(y).month(m)
        .endOf('month')
        .toISOString();
      countDay = dayjs(`${y}-${m + 1}`).daysInMonth() - 15;
      startDay = 16;
    }
    return { start, end, countDay, startDay };
  };

  useEffect(() => {
    const { start, end, countDay, startDay } = computeRange(year, month, selectedOption);
    setCountDay(countDay);
    setStartDay(startDay);
    setDateStart(start);
    setDateEnd(end);

    // จำงวด/ประเภทพนักงานล่าสุดไว้ (เฉพาะโหมดปกติ) ให้กลับมาหน้านี้รอบหน้าในเซสชันเดียวกันแล้วเห็นงวดเดิม
    if (!lockPeriod) {
      hrReportLastView = { month, year, typeSelect, selectedOption };
    }

    setIsDataLoading(true); // show overlay immediately — before debounce fires
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      refreshAll(start, end, typeSelect);
    }, 250);

    return () => clearTimeout(fetchTimerRef.current);
    // eslint-disable-next-line
  }, [month, year, selectedOption, typeSelect]);

  // มีข้อมูล timestamp อยู่จริงในช่วงนี้ไหม (ใช้ query ตัวเดิม /timestamp ที่มีอยู่แล้ว ไม่ต้องเพิ่ม endpoint ใหม่)
  // ใช้เช็คขอบเขตก่อนเลื่อนเดือน/งวด กันเลื่อนไปเดือน/งวดที่ไม่มี timestamp เลย
  const checkHasData = async (start, end, type) => {
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/timestamp?dateStart=${start}&dateEnd=${end}&type=${type || ''}&size=1`,
      );
      return (data?.total ?? 0) > 0;
    } catch (error) {
      console.error(error);
      return true; // เช็คไม่ได้ (เช่น เน็ตหลุด) → ปล่อยให้เลื่อนได้ ดีกว่าไปล็อกผู้ใช้ไว้
    }
  };

  // งวด/เดือนถัดไปหรือก่อนหน้า ตาม typeSelect — DAILY เลื่อนทีละงวด (1-15/16-สิ้นเดือน), อย่างอื่นเลื่อนทีละเดือน
  const getAdjacentPeriod = (dir) => {
    if (typeSelect === 'DAILY') {
      if (dir === 1) {
        if (selectedOption === '15') return { month, year, selectedOption: '31' };
        return month === 11
          ? { month: 0, year: year + 1, selectedOption: '15' }
          : { month: month + 1, year, selectedOption: '15' };
      }
      if (selectedOption === '31') return { month, year, selectedOption: '15' };
      return month === 0
        ? { month: 11, year: year - 1, selectedOption: '31' }
        : { month: month - 1, year, selectedOption: '31' };
    }
    if (dir === 1) {
      return month === 11
        ? { month: 0, year: year + 1, selectedOption }
        : { month: month + 1, year, selectedOption };
    }
    return month === 0
      ? { month: 11, year: year - 1, selectedOption }
      : { month: month - 1, year, selectedOption };
  };

  const [navBusy, setNavBusy] = useState(false);

  const navigatePeriod = async (dir) => {
    if (navBusy) return;
    const target = getAdjacentPeriod(dir);
    const { start, end } = computeRange(target.year, target.month, target.selectedOption);
    setNavBusy(true);
    const ok = await checkHasData(start, end, typeSelect);
    setNavBusy(false);
    if (!ok) return; // ชนขอบเขตข้อมูล — ไม่มี timestamp ในเดือน/งวดนี้ ไม่เลื่อน
    setMonth(target.month);
    setYear(target.year);
    if (target.selectedOption !== selectedOption) setSelectedOption(target.selectedOption);
  };

  useEffect(() => {
    // roletype ไม่ได้อ่านในไฟล์นี้ (ใช้แค่ที่อื่น) เป็น reference data ที่แทบไม่เปลี่ยน — ปล่อย
    // dispatch ปกติแม้ lockPeriod ความเสี่ยงเขียนทับต่ำมาก ไม่ใช่ต้นเหตุที่ข้อมูลหน้าต่างอื่นหาย
    dispatch(actions.roletypeAll({}));
    if (lockPeriod) {
      api.get(`${process.env.REACT_APP_API_URL}/employee?name=&size=50&page=1&permission=&activeOnly=false`)
        .then(({ data }) => setLocalEmployees(data)).catch(() => {});
      api.get(`${process.env.REACT_APP_API_URL}/project?name=&size=300&page=1&status_deliver=&profile=&time_tracking_enabled=&time_tracking_available=true&lite=`)
        .then(({ data }) => setLocalProject(data)).catch(() => {});
      api.get(`${process.env.REACT_APP_API_URL}/information/`)
        .then(({ data }) => setLocalInfo(data || null))
        .catch(() => {})
        .finally(() => setLocalInfoLoaded(true));
    } else {
      dispatch(actions.employeeAll({}));
      dispatch(actions.projectAll({ time_tracking_available: true }));
      dispatch(actions.getInformation({}));
    }
    // eslint-disable-next-line
  }, [lockPeriod]);

  const handlePrint = () => {
    // ส่งยอดที่คำนวณถูกต้องแล้วจากตารางเงินเดือน (รองรับ FULLTIME spd×30, override, วันลา ฯลฯ)
    // ไปให้ PDF ใช้แทนยอดดิบจาก backend เพื่อให้ตรงกับหน้าจอเป๊ะ — ใช้ displayProjectDashboard
    // (เหมือน <TimestampProjectCost> ด้านล่าง) ไม่ใช่ projectDashboard สด งวด locked จะได้ยอดตรง
    // กับที่บันทึกจริง ไม่ใช่ยอดคำนวณสด ณ ตอนกดพิมพ์
    const liveValues = getValues('salaryData') || [];
    // merge ยอดยกมาเข้าคอลัมน์ปลายทาง (จับคู่ empId ผ่าน salaryData ตาม index) ให้ PDF ตรงกับที่บันทึก
    const liveSalaryData = liveValues.map((v, i) => mergeCarryoverRow(v, carryoverByEmpId[String(salaryData[i]?.employee_id)]));
    timeStampMontlyReport(info, timestamp, days, month, year, dateStart, leaveRequest?.rows || [], liveSalaryData, displayProjectDashboard);
  };

  // ปุ่มพิมพ์ฉบับเต็ม — ยก MergedPayrollTableV2 (แยก 2 ตาราง: ข้อมูลลงเวลา / การคิดเงิน) +
  // TimestampProjectCost มาไว้ใน PDF เดียว ใช้ข้อมูลชุดเดียวกับที่หน้าจอนี้แสดงเป๊ะๆ ทุกตัวเลข
  const handlePrintFull = () => {
    const liveValues = getValues('salaryData') || [];
    // getValues คืนค่าเฉพาะฟิลด์ที่ผูก Controller ไว้ (revenue.*/expenses.*) — employee_id ไม่มี Controller
    // เลยหายไปจาก getValues ทำให้ match ด้วย employee_id ไม่เจอ (ยอดออกมา 0 หมด) ต้อง merge กับ
    // salaryData state เดิม (มี employee_id ครบ) โดยจับคู่ตาม index แทน แล้วค่อยส่ง employee_id ไปด้วย
    const liveSalaryData = salaryData.map((row, i) => mergeCarryoverRow({
      ...row,
      ...(liveValues[i] || {}),
      revenue: { ...row.revenue, ...(liveValues[i]?.revenue || {}) },
      expenses: { ...row.expenses, ...(liveValues[i]?.expenses || {}) },
      employee_id: row.employee_id,
    }, carryoverByEmpId[String(row.employee_id)]));
    hrReportFullPDF({
      info,
      month,
      year,
      dateStart,
      days,
      timestamp,
      empSummary,
      liveSalaryData,
      leaveData: leaveRequest?.rows || [],
      // displayProjectDashboard เหมือน <TimestampProjectCost> บนจอ — ไม่ใช่ projectDashboard สด
      // งวด locked จะได้ยอดตรงกับที่บันทึกจริง (snapshot) แทนยอดคำนวณสด ณ ตอนกดพิมพ์ ซึ่งอาจ
      // ต่างจากที่บันทึกจริงได้ (เช่น field_allowance ที่ขึ้นกับ fieldAllowanceMode ปัจจุบัน)
      projectDashboard: displayProjectDashboard,
      holidayDaySet,
      salaryMode,
    });
  };

  // headless auto-print (ปุ่ม "พิมพ์" ในหน้ารายการเงินเดือน) — รอจน salaryData โหลดเสร็จ +
  // empSummary คำนวณมาจาก MergedPayrollTableV2 ครบแล้ว (ต้อง mount ตารางจริงก่อนถึงจะได้ค่านี้
  // ไม่มี endpoint คำนวณสูตรพวกนี้ตรงๆ) ค่อยเรียกพิมพ์ให้เองครั้งเดียว แล้วแจ้งผู้เรียกให้ unmount ทิ้ง
  const autoPrintFiredRef = useRef(false);
  useEffect(() => {
    if (!autoPrintOnReady || autoPrintFiredRef.current) return;
    if (isDataLoading || salaryData.length === 0) return;
    if (!empSummary || Object.keys(empSummary).length === 0) return;
    autoPrintFiredRef.current = true;
    handlePrintFull();
    onAutoPrintDone?.();
    // eslint-disable-next-line
  }, [autoPrintOnReady, isDataLoading, salaryData, empSummary]);

  const dashboardReadyFiredRef = useRef(false);
  // เก็บ callback ล่าสุดไว้ใน ref — ไม่ใส่ onDashboardReady ใน deps ของ effect ด้านล่าง (ผู้เรียกอาจสร้างใหม่บ่อย ทำให้ timer รอยอดนิ่งถูกรีเซ็ตซ้ำ)
  const onDashboardReadyRef = useRef(onDashboardReady);
  onDashboardReadyRef.current = onDashboardReady;
  useEffect(() => {
    if (!onDashboardReady || dashboardReadyFiredRef.current) return undefined;
    if (isDataLoading || salaryData.length === 0 || !periodChecked || !crlLoaded || !carryoverLoaded) return undefined;
    if (!empSummary || Object.keys(empSummary).length === 0) return undefined;
    // รอให้ยอดนิ่งก่อนส่ง: หลังข้อมูลโหลดครบ ตารางยังต้อง sync ค่าคำนวณอัตโนมัติ (ปกส./ค่าแรง/ฯลฯ) ลงฟอร์มอีกหลายรอบ
    // — effect นี้รันก่อนที่ยอดเหล่านั้นจะอัปเดต ถ้าส่งทันทีจะได้ยอดเก่า ทุกครั้งที่ displayProjectDashboard เปลี่ยน
    // timer จะเริ่มนับใหม่ ส่งเมื่อไม่มีการเปลี่ยนแปลงต่อเนื่อง 700 ms
    // ส่งยอดชุดเดียวกับที่ <TimestampProjectCost> แสดงบนจอ (displayProjectDashboard: งวดที่ล็อกใช้ snapshot ที่บันทึกจริง)
    // ไม่ใช่ costResult.rows ที่คำนวณสดเสมอ
    const timer = setTimeout(() => {
      if (dashboardReadyFiredRef.current) return;
      dashboardReadyFiredRef.current = true;
      onDashboardReadyRef.current(computeProjectCostRows(displayProjectDashboard).rows);
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [!!onDashboardReady, isDataLoading, salaryData, empSummary, periodChecked, crlLoaded, carryoverLoaded, displayProjectDashboard]);

  const handleValueChange = (event) => setSelectedOption(event.target.value);

  const handlePreviousMonth = () => navigatePeriod(-1);
  const handleNextMonth = () => navigatePeriod(1);

  const handleChangeMonth = (event) => setMonth(parseInt(event.target.value));
  const handleChangeYear = (event) => setYear(parseInt(event.target.value));

  const toAdjNum = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
  const buildAdjPayload = useCallback((empId, row) => ({
    employee_id: String(empId),
    dateStart,
    dateEnd,
    revenue: {
      allowances: toAdjNum(row?.revenue?.allowances),
      bonus: toAdjNum(row?.revenue?.bonus),
      overtime: toAdjNum(row?.revenue?.overtime),
      field_allowance: toAdjNum(row?.revenue?.field_allowance),
    },
    expenses: {
      tax: toAdjNum(row?.expenses?.tax),
      other: toAdjNum(row?.expenses?.other),
      sso: toAdjNum(row?.expenses?.sso),
      late: toAdjNum(row?.expenses?.late),
    },
    note: row?.note?.trim() || null,
    manual_fields: row?.manual_fields || [],
  }), [dateStart, dateEnd]);

  const onAdjSave = useCallback((empId) => {
    if (effectiveReadOnly) return;
    const fi = salaryData.findIndex((f) => String(f.employee_id) === String(empId));
    if (fi === -1) return;
    const row = getValues(`salaryData.${fi}`);
    markEdited();
    dispatch(actions.payrollAdjUpsert(buildAdjPayload(empId, row)));
  }, [salaryData, getValues, dispatch, effectiveReadOnly, buildAdjPayload, markEdited]);

  const handleOpenModal = useCallback((data) => {
    setSelectEachEmployee(data);
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectEachEmployee('');
  };

  // ไม่ markEdited() ตรงนี้ (ต่างจากตอนแรกที่ทำ) — dispatcher พวกนี้ไม่ dispatch อะไรลง redux เอง แค่ยิง
  // PATCH ตรงๆ แล้ว MergedPayrollTableV2.js เป็นคน await onRefreshTimestamp() แยกทีหลังเสมอ ถ้า mark
  // edited ตั้งแต่ตรงนี้ debounce timer จะเริ่มนับก่อนข้อมูลจริงอัปเดตตาม (ดู markEdited() ใน
  // refreshTimestampOnly แทน — ย้ายไปเรียกที่นั่นหลัง refresh เสร็จแล้วจริง กันยิง sync ด้วยยอดเก่า)
  const onSalaryOverride = useCallback((id, payload) => (effectiveReadOnly ? undefined : dispatch(actions.timestampSalaryOverride(id, payload))), [dispatch, effectiveReadOnly]);
  const onSkipLateDeduct = useCallback((id, payload) => (effectiveReadOnly ? undefined : dispatch(actions.timestampSkipLate(id, payload))), [dispatch, effectiveReadOnly]);
  const onCheckInTimeUpdate = useCallback((id, payload) => (effectiveReadOnly ? undefined : dispatch(actions.timestampUpdateCheckInTime(id, payload))), [dispatch, effectiveReadOnly]);
  const onProjectUpdate = useCallback((id, payload) => (effectiveReadOnly ? undefined : dispatch(actions.timestampUpdateProject(id, payload))), [dispatch, effectiveReadOnly]);
  const onCheckInTimeClear = useCallback((id) => (effectiveReadOnly ? undefined : dispatch(actions.timestampClearTimeOverride(id))), [dispatch, effectiveReadOnly]);
  const onProjectClear = useCallback((id) => (effectiveReadOnly ? undefined : dispatch(actions.timestampClearProjectOverride(id))), [dispatch, effectiveReadOnly]);
  const onHourOverride = useCallback((id, payload) => (effectiveReadOnly ? undefined : dispatch(actions.timestampHourOverride(id, payload))), [dispatch, effectiveReadOnly]);
  const onOtOverride = useCallback((id, payload) => (effectiveReadOnly ? undefined : dispatch(actions.timestampToggleOtOverride(id, payload))), [dispatch, effectiveReadOnly]);

  const [isTableLoading, setIsTableLoading] = useState(false);
  const [tableDataVersion, setTableDataVersion] = useState(0);
  const [showFormulas, setShowFormulas] = useState(false);
  const [salaryMode, setSalaryMode] = useState('work-time');

  const refreshTimestampOnly = useCallback(async () => {
    setIsTableLoading(true);
    try {
      if (lockPeriod) {
        const [dashboardRes, payrollRes] = await Promise.all([
          api.get(`${process.env.REACT_APP_API_URL}/timestamp/timestamp-payroll-dashboard?dateStart=${dateStart}&dateEnd=${dateEnd}&typeSelect=${typeSelect}`),
          api.get(`${process.env.REACT_APP_API_URL}/timestamp/timestamp-payroll?dateStart=${dateStart}&dateEnd=${dateEnd}&typeSelect=${typeSelect}`),
        ]);
        setLocalTimestamp({ ...dashboardRes.data, isCompleted: true });
        setLocalTimestampPayroll(payrollRes.data);
      } else {
        await dispatch(actions.timestampPayrollFetchBoth({ dateStart, dateEnd, typeSelect }));
      }
      await new Promise((r) => setTimeout(r, 80));
      setTableDataVersion((v) => v + 1);
      // markEdited() ตรงนี้ (ไม่ใช่ตอนกด dispatcher เอง) เพราะ dispatcher ส่วนใหญ่ (onHourOverride/
      // onSalaryOverride/onSkipLateDeduct/onCheckInTimeUpdate/onCheckInTimeClear/onProjectUpdate/
      // onProjectClear/onOtOverride) ไม่ dispatch อะไรลง redux เอง — MergedPayrollTableV2.js เป็นคน
      // await onRefreshTimestamp() (= ฟังก์ชันนี้) แยกต่างหากหลัง dispatcher สำเร็จเสมอ ถ้า mark edited
      // ตั้งแต่ตอนเรียก dispatcher (ก่อนหน้านี้) ตัวจับเวลา debounce 2.5 วิจะเริ่มนับตั้งแต่ก่อนข้อมูลจริง
      // (timestamp/projectDashboard/costResult) จะอัปเดตตามเลย — ถ้า refresh รอบนี้ใช้เวลานานกว่า 2.5 วิ
      // (เช่น เครือข่ายช้า/backend โหลดหนัก) debounce จะยิง sync ไปด้วยยอดเก่าที่ยังไม่รวมการแก้ไขล่าสุด แล้ว
      // ไม่มีการ sync ซ้ำอีกเพราะ editedTick ไม่ขยับต่อ (พบจากที่ user รายงานว่าเพิ่มชั่วโมงแบบ manual
      // แล้วไม่เห็นรายการเบิกถูกสร้าง) — ย้ายมาเรียกที่นี่แทน รับประกันว่า costResult สดแล้วจริงก่อนเริ่มนับเวลา
      markEdited();
    } finally {
      setIsTableLoading(false);
    }
  }, [dispatch, dateStart, dateEnd, typeSelect, lockPeriod, markEdited]);

  // markEdited() อยู่ใน .then() หลัง refreshAll เสร็จจริง (ไม่ใช่ก่อน dispatch เหมือนตอนแรกที่ทำ) — เหตุผล
  // เดียวกับ onHourOverride ฯลฯ ด้านบน: refreshAll ทำ API call เพิ่ม (ไม่ใช่แค่ manualWorkUpsert เฉยๆ)
  // กว่า costResult จะอัปเดตตามข้อมูลใหม่จริงต้องรอ refreshAll เสร็จก่อน ถ้า mark edited ตั้งแต่ก่อน dispatch
  // ตัวจับเวลา debounce อาจครบก่อนข้อมูลอัปเดต แล้วยิง sync ด้วยยอดเก่า (ไม่มีรอบสองเพราะ editedTick ไม่ขยับต่อ)
  const onManualSave = useCallback(
    (payload) => {
      if (effectiveReadOnly) return undefined;
      return dispatch(actions.manualWorkUpsert(payload))
        .then(() => refreshAll(dateStart, dateEnd, typeSelect, true))
        .then(() => markEdited());
    },
    [dispatch, refreshAll, dateStart, dateEnd, typeSelect, effectiveReadOnly, markEdited],
  );

  const onManualDelete = useCallback(
    (id) => {
      if (effectiveReadOnly) return undefined;
      return dispatch(actions.manualWorkDelete(id))
        .then(() => refreshAll(dateStart, dateEnd, typeSelect, true))
        .then(() => markEdited());
    },
    [dispatch, refreshAll, dateStart, dateEnd, typeSelect, effectiveReadOnly, markEdited],
  );

  // สร้าง PDF ใบสรุปค่าแรงต่อโครงการ (laborProjectReceiptPDF) แล้วแนบเข้า bill_images ของ Expense ค่าแรง
  // ที่เพิ่ง flip เป็น PENDING — เรียกหลังบันทึกเงินเดือนสำเร็จเท่านั้น (ไม่ใช่ตอนยัง draft เพราะ generate/
  // upload ทุกรอบ debounce sync จะช้า+เปลืองพื้นที่เก็บไฟล์เก่าทิ้งไปเรื่อยๆ โดยไม่มีประโยชน์)
  const attachLaborReceipts = useCallback(async (laborExpenses, dashboardRows = costResult.rows) => {
    const failedProjects = [];
    await Promise.all((laborExpenses || []).map(async ({ project_id: pid, expense_id: expenseId, code, employee_id: splitEmpId, amount: expenseAmount }) => {
      try {
        // dashboardRows = ยอดชุดเดียวกับที่บันทึกจริง (ปรับยอดยกไปงวดหน้าแล้ว — ดู onSubmit)
        const fullRow = dashboardRows.find((r) => String(r.project_id) === String(pid));
        if (!fullRow) return;
        // โครงการนี้มีรายการแยกรายคน (พนักงานที่ติ๊ก "ไม่จ่ายเงินตามโครงการ") — ใบสรุปของแต่ละรายการแสดงเฉพาะคนที่
        // อยู่ในรายการนั้น: รายการรายคน = คนนั้นคนเดียว, รายการปกติ = ทุกคนยกเว้นคนที่แยกออกไป
        const splitIds = new Set((laborExpenses || [])
          .filter((x) => String(x.project_id) === String(pid) && x.employee_id)
          .map((x) => String(x.employee_id)));
        let projectRow = fullRow;
        if (splitIds.size > 0) {
          const keep = (id) => (splitEmpId ? String(id) === String(splitEmpId) : !splitIds.has(String(id)));
          projectRow = {
            ...fullRow,
            employees: (fullRow.employees || []).filter((e) => keep(e.employee_id)),
            employee_deductions: (fullRow.employee_deductions || []).filter((d) => keep(d.employee_id)),
            ...(expenseAmount != null && { total_payable: expenseAmount }),
          };
        }
        const blob = await laborProjectReceiptPDF({
          code,
          projectRow,
          dateStart,
          dateEnd,
          empType: typeSelect,
          me,
          open: false,
        });
        const formData = new FormData();
        formData.append('file', blob, `labor-receipt-${expenseId}.pdf`);
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        await dispatch(actions.expensesPut(expenseId, { bill_images: [data.id] }));
      } catch (error) {
        console.error('[attachLaborReceipts]', error);
        failedProjects.push(projectDashboard.find((p) => String(p.project_id) === String(pid))?.project_number || pid);
      }
    }));
    // แจ้งเตือนถ้าแนบ PDF หลักฐานไม่สำเร็จ — เดิมพลาดแล้วเงียบ ไม่มีใครรู้ว่ารายการค่าแรงกลายเป็น PENDING
    // แล้วแต่ไม่มีไฟล์แนบเลย (ไม่บล็อกการบันทึกเงินเดือน/navigate หลัก — แค่แจ้งเป็น background หลังทำเสร็จ)
    if (failedProjects.length) {
      showAlert(`แนบ PDF หลักฐานค่าแรงไม่สำเร็จสำหรับโครงการ: ${failedProjects.join(', ')} — กรุณาแนบไฟล์ด้วยตนเองในแท็บ "ตั้งเบิกค่าแรง"`);
    }
  }, [costResult, projectDashboard, dateStart, dateEnd, typeSelect, me, dispatch]);

  const onSubmit = async () => {
    if (effectiveReadOnly) {
      showAlert('งวดนี้บันทึกเงินเดือนไปแล้ว ไม่สามารถบันทึกซ้ำได้');
      return;
    }

    // เช็คก่อนบันทึกจริง — โครงการไหนมีรายการเบิกค่าแรงงวดนี้ที่อนุมัติ/ดำเนินการไปแล้ว (ระบบจะไม่แตะยอดซ้ำ
    // ดู safety guard ใน upsertLaborExpenseLocked) แต่ยอดในตารางตอนนี้ต่างจากยอดที่อนุมัติค้างไว้ — เตือน HR
    // ก่อนกดบันทึกจริง แทนที่จะรู้ทีหลังตอนงวดล็อกไปแล้วแก้อะไรไม่ได้แล้ว (เดิม submitLaborDraftsToPending
    // แค่ "ข้าม" เงียบๆ แล้วบอกหลังบันทึกสำเร็จว่าข้าม ไม่บอกด้วยซ้ำว่ายอดต่างกันเท่าไหร่)
    const conflictCheckRows = (costResult.rows || [])
      .filter((r) => r.project_id)
      .map((r) => ({
        project_id: r.project_id,
        project_number: r.project_number,
        total_payable: r.total_payable || 0,
        employee_payables: r.employee_payables || [],
      }));
    if (conflictCheckRows.length > 0) {
      const conflicts = await dispatch(actions.checkLaborDraftConflicts({
        dateStart,
        dateEnd,
        typeSelect,
        rows: conflictCheckRows,
      }));
      if (conflicts?.length) {
        const fmtBaht = (n) => (Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
        const lines = conflicts
          .map((c) => `${c.project_number || 'ไม่ระบุโครงการ'}: อนุมัติไว้แล้ว ${fmtBaht(c.existing_amount)} บาท แต่ตารางตอนนี้คำนวณได้ ${fmtBaht(c.new_amount)} บาท (${c.diff > 0 ? 'เพิ่มขึ้น' : 'ลดลง'} ${fmtBaht(Math.abs(c.diff))} บาท)`)
          .join('\n');
        const proceed = await confirmAsync(
          `พบโครงการที่ยอดในตารางไม่ตรงกับรายการเบิกค่าแรงที่อนุมัติ/ดำเนินการไปแล้ว (ระบบจะไม่แก้ยอดที่อนุมัติแล้วให้อัตโนมัติ):\n\n${lines}\n\nต้องการบันทึกเงินเดือนต่อไปหรือไม่? (ต้องไปจัดการยอดที่ต่างกันเองภายหลังในแท็บ "ตั้งเบิกค่าแรง")`,
        );
        if (!proceed) return;
      }
    }

    // merge base salaryData with any RHF overrides from editable inputs in the table
    const mergedData = salaryData.map((row, fi) => {
      const ov = getValues(`salaryData.${fi}`) || {};
      return {
        ...row,
        revenue: { ...row.revenue, ...(ov.revenue || {}) },
        expenses: { ...row.expenses, ...(ov.expenses || {}) },
        note: ov.note !== undefined ? ov.note : row.note,
        manual_fields: ov.manual_fields !== undefined ? ov.manual_fields : (row.manual_fields || []),
      };
    });

    const isWorkTime = salaryMode === 'work-time';
    // ยอดยกมาลงงวดนี้ — บวก/ลบสุทธิเข้ายอด "ที่ต้องจ่าย"/สุทธิ (ไม่ merge เข้าตัวเลขในคอลัมน์)
    const carryDelta = (c) => (Number(c?.addTotal) || 0) - (Number(c?.deductTotal) || 0);

    // ── โหมด auto: หาคนที่ "ที่ต้องจ่ายพนักงาน" ติดลบ (สูตรเดียวกับ ActualNetCell — ไม่หักสาย) ──
    // ยอดยกมางวดนี้รวมอยู่ใน net แล้ว (chain ต่อไปงวดถัดไปได้ถ้ายังไม่หมด)
    const actualNetOf = (item) => {
      const empId = String(item?.employee_id);
      const actualSal = empSummary[empId]?.actualSalaryAmt || 0;
      const ot = parseFloat(item?.revenue?.overtime || 0);
      const all = parseFloat(item?.revenue?.allowances || 0);
      const fa = parseFloat(item?.revenue?.field_allowance || 0);
      const bon = parseFloat(item?.revenue?.bonus || 0);
      const tax = parseFloat(item?.expenses?.tax || 0);
      const sso = parseFloat(item?.expenses?.sso || 0);
      const crl = parseFloat(item?.expenses?.crl || 0);
      const oth = parseFloat(item?.expenses?.other || 0);
      return actualSal + ot + all + fa + bon - tax - sso - crl - oth + carryDelta(item?.carryover);
    };
    // ค่าเริ่มต้นของช่อง "งวดนี้จ่าย" ใน CarryoverPromptDialog = "ที่ต้องจ่ายพนักงาน" แบบไม่นับหัก CRL และหักอื่นๆ
    // (รวมยอดยกมาด้วย ยกเว้นส่วนที่ยกมาลงหัก CRL/หักอื่นๆ) — ไม่ต่ำกว่า 0
    const payDefaultOf = (item) => {
      const byField = item?.carryover?.byField || {};
      return Math.max(0, actualNetOf(item)
        + parseFloat(item?.expenses?.crl || 0)
        + parseFloat(item?.expenses?.other || 0)
        + (Number(byField['expenses.crl']) || 0)
        + (Number(byField['expenses.other']) || 0));
    };
    const negatives = mergedData
      .filter((item) => actualNetOf(item) < -0.005)
      .map((item) => ({
        employee_id: String(item.employee_id),
        name: item.name || employees?.rows?.find((e) => String(e._id) === String(item.employee_id))?.name || item.employee_id,
        amount: Math.round(-actualNetOf(item) * 100) / 100,
        gross: Math.round(payDefaultOf(item) * 100) / 100,
      }));

    let carryoverOptIn = [];
    if (negatives.length > 0) {
      const optIn = await askCarryoverOptIn(negatives);
      if (optIn === null) return; // HR กด "ยกเลิก" — ไม่บันทึก
      carryoverOptIn = optIn;
    }

    // ตั้งแต่จุดนี้ merge "ยอดยกมา" เข้าคอลัมน์ปลายทางจริง — ยอดสุทธิ/ยอดนำส่งภาษี-ปกส./ยอดต่อคน
    // ที่บันทึกจึงรวมยอดยกมาโดยไม่ต้องบวก carryDelta แยกอีก (การเช็คติดลบด้านบนยังใช้ mergedData ดิบ
    // + carryDelta เพื่อให้ตรงกับ ActualNetCell บนจอเป๊ะ)
    const submitData = mergedData.map((row) => mergeCarryoverRow(row, row.carryover));

    const total_tax = submitData.reduce((sum, item) => sum + parseFloat(item?.expenses?.tax || 0), 0);
    const total_sso = submitData.reduce((sum, item) => sum + parseFloat(item?.expenses?.sso || 0), 0);
    const total_late = isWorkTime ? 0 : submitData.reduce((sum, item) => sum + parseFloat(item?.expenses?.late || 0), 0);
    const total_crl = submitData.reduce((sum, item) => sum + parseFloat(item?.expenses?.crl || 0), 0);
    // ยอดยกไปงวดหน้า (บังคับทุกคนที่ติดลบ — ห้ามบันทึกติดลบ) = ยอดติดลบ + ยอดที่ HR เลือกจ่ายงวดนี้ (0 ถ้าเลือก "0")
    // บวกยอดนี้คืนในงวดนี้ → ที่ต้องจ่ายงวดนี้ = ยอดที่เลือกจ่าย (0 หรือยอดที่กรอก) ส่วนที่ยกไปหักใน "หักอื่นๆ"
    // ของงวดหน้าแทน ไม่งั้นยอดติดลบถูกหักทั้งงวดนี้และงวดหน้าซ้ำ (ต้นทุนค่าแรงโครงการขาดไปเท่ายอดติดลบ)
    const carryOutByEmp = new Map(
      carryoverOptIn.map((o) => [String(o.employee_id), Number(o.amount) || 0]),
    );
    const totalCarryOut = [...carryOutByEmp.values()].reduce((s, v) => s + v, 0);
    const totalNet = totalCarryOut + submitData.reduce((sum, item) => {
      const salary = parseFloat(item?.revenue?.salary || 0);
      const overtime = parseFloat(item?.revenue?.overtime || 0);
      const allowances = parseFloat(item?.revenue?.allowances || 0);
      const bonus = parseFloat(item?.revenue?.bonus || 0);
      const tax = parseFloat(item?.expenses?.tax || 0);
      const sso = parseFloat(item?.expenses?.sso || 0);
      const late = isWorkTime ? 0 : parseFloat(item?.expenses?.late || 0);
      const crl = parseFloat(item?.expenses?.crl || 0);
      const other = parseFloat(item?.expenses?.other || 0);
      const field_allowance = parseFloat(item?.revenue?.field_allowance || 0);
      return sum + salary + overtime + allowances + bonus + field_allowance - tax - sso - late - crl - other;
    }, 0);

    // ใช้ projectDashboard ตัวเดียวกับที่โชว์บนจอ (TimestampProjectCost) ไม่ใช่ timestampPayroll.dashboard
    // ที่คำนวณคนละสูตร คนละจังหวะ (เป็นต้นเหตุที่ตัวเลขบันทึกจริงไม่ตรงกับที่ HR เห็นก่อนกดบันทึก)
    // total_payable ต่อโครงการมากับ projectDashboard อยู่แล้ว (คำนวณที่ MergedPayrollTableV2.js)
    // costResult ตัวเดียวกับที่ debounce sync ค่าแรงด้านบนใช้ (hoisted useMemo) — ไม่คำนวณซ้ำ
    // ปรับ total_payable ต่อโครงการตามยอดยกไปงวดหน้า — costResult บวกคืนยอดยกไปที่ "มีอยู่แล้ว" (outgoing จาก
    // การบันทึกงวดนี้ครั้งก่อน) ไว้ที่โครงการหลักแล้ว → ปรับเฉพาะส่วนต่าง (ยอดที่เลือกรอบนี้ − ยอดเดิม) ที่โครงการหลัก
    // ของคนนั้น (dominant_employee_ids จาก MergedPayrollTableV2)
    const carryOutDeltaByEmp = new Map();
    new Set([...carryOutByEmp.keys(), ...Object.keys(carryoverOutByEmpId)]).forEach((empId) => {
      const delta = (carryOutByEmp.get(empId) || 0) - sumCarryOut(carryoverOutByEmpId[empId]);
      if (Math.abs(delta) > 0.005) carryOutDeltaByEmp.set(empId, delta);
    });
    const dashboardRows = costResult.rows.map((r) => ({ ...r }));
    carryOutDeltaByEmp.forEach((delta, empId) => {
      const target = dashboardRows.find((r) => (r.dominant_employee_ids || []).includes(empId))
        || dashboardRows.find((r) => (r.employee_deductions || []).some((d) => String(d.employee_id) === empId))
        || dashboardRows[0];
      if (target) {
        target.total_payable = Math.round(((target.total_payable || 0) + delta) * 100) / 100;
        // ปรับยอดรายคน (employee_payables) คู่กันด้วย ให้ผลรวมรายคนใน snapshot = total_payable ของโครงการเสมอ
        const list = (target.employee_payables || []).map((e) => ({ ...e }));
        const entry = list.find((e) => String(e.employee_id) === empId);
        if (entry) entry.amount = Math.round(((entry.amount || 0) + delta) * 100) / 100;
        else list.push({ employee_id: empId, amount: Math.round(delta * 100) / 100 });
        target.employee_payables = list;
      }
    });
    const total_payable = dashboardRows.reduce((sum, r) => sum + (r.total_payable || 0), 0);

    const dataSubmit = {
      dateStart,
      dateEnd,
      role_type: typeSelect,
      total_tax,
      total_sso,
      total_late,
      total_crl,
      total_payable,
      total: totalNet,
      // ยอดยกไปงวดหน้า (โหมด auto) — ส่งเสมอ (แม้ []) เพื่อให้ backend reconcile:
      // คนที่ opt-in = สร้าง/อัปเดตยอดค้าง, คนที่ไม่มี = ยกเลิกยอดค้างเก่าของงวดนี้ (เคสคีย์ใหม่แล้วไม่ติดลบ)
      carryoverOptIn,
      timestampList: submitData.flatMap((e) => (e.checkins || []).map((c) => c?._id || c)),
      salaryData: submitData.map((row) => {
        const { carryover, ...eachdata } = row;
        return {
          ...eachdata,
          employee: eachdata?.employee_id,
          payrollType: typeSelect,
          sso_percentage: eachdata?.sso,
          expenses: {
            ...eachdata?.expenses,
            late: isWorkTime ? 0 : parseFloat(eachdata?.expenses?.late || 0),
            crl: parseFloat(eachdata?.expenses?.crl || 0),
          },
          total: parseFloat(eachdata?.revenue?.salary || 0)
            + parseFloat(eachdata?.revenue?.overtime || 0)
            + parseFloat(eachdata?.revenue?.allowances || 0)
            + parseFloat(eachdata?.revenue?.bonus || 0)
            + parseFloat(eachdata?.revenue?.field_allowance || 0)
            - parseFloat(eachdata?.expenses?.other || 0)
            - parseFloat(eachdata?.expenses?.tax || 0)
            - parseFloat(eachdata?.expenses?.sso || 0)
            - parseFloat(eachdata?.expenses?.crl || 0)
            - (isWorkTime ? 0 : parseFloat(eachdata?.expenses?.late || 0))
            + (carryOutByEmp.get(String(eachdata?.employee_id)) || 0),
          // ยอดยกไปงวดหน้า (= ยอดติดลบ + ยอดที่เลือกจ่ายงวดนี้) บวกคืนใน total ด้านบนแล้ว — งวดนี้เหลือเท่ายอดที่เลือกจ่าย
          carryover_out: carryOutByEmp.get(String(eachdata?.employee_id)) || 0,
          // ยอดยกมาที่ใช้ในงวดนี้ (snapshot audit — ยอดถูก merge เข้าคอลัมน์ด้านบนแล้ว
          // backend mark applied เองจาก target_period)
          carryover_applied: (carryover?.items || []).map((it) => ({
            id: it._id || it.id,
            amount: it.amount,
            target_field: it.target_field,
            direction: it.direction,
            source: it.source,
          })),
        };
      }),
      dashboard: dashboardRows,
      note,
    };

    setSalaryLoading(true);
    try {
      // sync ร่าง (PayrollAdjustment) ให้ตรงกับค่าที่กำลังจะบันทึกจริงทุกคน ก่อนบันทึกเงินเดือน
      // กันปัญหา "หักอื่นๆ"/"หมายเหตุ" หายถาวรถ้ามีคนลบ record เงินเดือนทีหลัง (ร่างเดิมอาจไม่ทันเซฟตอนพิมพ์)
      const adjPayloads = mergedData.map((row) => buildAdjPayload(row.employee_id, row));
      await Promise.all(adjPayloads.map((p) => dispatch(actions.payrollAdjUpsert(p)).catch(() => {})));

      const created = await dispatch(actions.salaryListCreate(dataSubmit));
      // แนบ PDF ใบสรุปค่าแรงต่อโครงการเข้า bill_images ของ Expense ค่าแรงที่เพิ่ง flip เป็น PENDING
      // (backend คืน project_id/expense_id คู่กันมาใน laborExpenses) — ทำเป็น background ไม่ await
      // ก่อน navigate เพราะไม่ใช่ผลลัพธ์หลักของการบันทึกเงินเดือน พลาดได้ไม่กระทบผลบันทึกจริง
      if (created?.laborExpenses?.length) {
        attachLaborReceipts(created.laborExpenses, dashboardRows);
      }
      setLaborStatusTick((t) => t + 1);
      // มีโครงการที่สร้างรายการเบิกค่าแรงไม่สำเร็จ — เตือน HR ให้ไปสร้างเองในแท็บ "ตั้งเบิกค่าแรง"
      // (เดิม backend กลืน error เงียบ ทำให้บางงวดได้รายการเบิกไม่ครบทุกโครงการโดยไม่มีใครรู้)
      if (created?.laborErrors?.length) {
        const labels = created.laborErrors
          .map((e) => e.project_number || e.project_id || 'ไม่ระบุโครงการ')
          .join(', ');
        showAlert(`สร้างรายการเบิกค่าแรงไม่สำเร็จสำหรับโครงการ: ${labels} — กรุณาสร้าง/ตรวจสอบด้วยตนเองในแท็บ "ตั้งเบิกค่าแรง"`);
      }
      // มีโครงการที่ "ข้าม" เพราะมีรายการเบิกค่าแรงงวดนี้อยู่แล้วในสถานะที่ระบบไม่แตะซ้ำ (อนุมัติ/เตรียมจ่าย/
      // จ่ายแล้ว/ปฏิเสธ/พัก) — มักเกิดจากเคยบันทึก+ลบงวดนี้มาก่อนแล้วรายการเก่ายังค้างอยู่ ต้องไปจัดการเอง
      if (created?.laborSkipped?.length) {
        const labels = created.laborSkipped
          .map((s) => `${s.project_number || s.project_id || 'ไม่ระบุ'} (${s.code || '-'} · ${s.existing_status || '-'})`)
          .join(', ');
        showAlert(`ข้ามการสร้างรายการเบิกค่าแรง เพราะมีรายการเดิมของงวดนี้ค้างอยู่แล้ว: ${labels} — ตรวจสอบ/จัดการในแท็บ "ตั้งเบิกค่าแรง" หากต้องการให้ตรงกับยอดงวดนี้`);
      }
      dispatch(actions.salaryListAll({}));
      history.push('/humen/salarylist');
    } finally {
      setSalaryLoading(false);
    }
  };

  // ── renders ──────────────────────────────────────────────────────────────────

  const renderCompactHeader = () => {
    // FULLTIME จ่ายเดือนละครั้ง → มีแค่ "ทั้งเดือน"
    // DAILY จ่าย 2 งวด/เดือน → มีแค่ "1-15" / "16-30" (ไม่มี "ทั้งเดือน" กันงวดคาบเกี่ยวกับสถานะล็อก)
    // ทั้งหมด (typeSelect ว่าง) → ดูรวมได้ทั้ง 3 แบบตามเดิม (เป็นมุมมองดูอย่างเดียว ไม่ผูกกับการบันทึก)
    const periodOpts =
      typeSelect === 'FULLTIME'
        ? [{ value: 'Month', label: 'ทั้งเดือน' }]
        : typeSelect === 'DAILY'
        ? [
            { value: '15', label: '1-15' },
            { value: '31', label: `16-${daysInMonth}` },
          ]
        : [
            { value: 'Month', label: 'ทั้งเดือน' },
            { value: '15', label: '1-15' },
            { value: '31', label: `16-${daysInMonth}` },
          ];
    return (
      <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr', alignItems: 'center', gap: 8 }}>
        <div style={{ ...S.row, ...mobileRow }}>
          {lockPeriod ? (
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {MONTH_NAMES[month]} {year}
              {' · '}
              {selectedOption === 'Month' ? 'ทั้งเดือน' : selectedOption === '15' ? '1-15' : `16-${daysInMonth}`}
            </span>
          ) : (
            <>
              <select value={month} onChange={handleChangeMonth} style={{ ...S.select, ...mobileTap, minWidth: 120 }}>
                {MONTH_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
              </select>

              <select value={year} onChange={handleChangeYear} style={{ ...S.select, ...mobileTap, minWidth: 80 }}>
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 4 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              <div style={{ display: 'inline-flex', border: `1px solid ${DIV}`, borderRadius: 4, overflow: 'hidden' }}>
                {periodOpts.map(({ value, label }, idx) => (
                  <button
                    key={value}
                    onClick={() => value !== selectedOption && handleValueChange({ target: { value } })}
                    style={{
                      padding: '4px 8px',
                      border: 'none',
                      borderRight: idx < periodOpts.length - 1 ? `1px solid ${DIV}` : 'none',
                      background: selectedOption === value ? PRIMARY : 'transparent',
                      color: selectedOption === value ? '#fff' : TEXT,
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                    }}
                  >{label}</button>
                ))}
              </div>
            </>
          )}
        </div>

        {!lockPeriod && (
          <Paper
            elevation={0}
            sx={{
              justifySelf: 'center',
              maxWidth: '100%',
              overflowX: 'auto',
              display: 'flex',
              gap: 0.5,
              p: 0.5,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: (theme) => (theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.35)'
                : '0 4px 20px rgba(15,23,42,0.06)'),
            }}
          >
            {TYPE_TABS.map((t) => {
              const selected = typeSelect === t.value;
              const Icon = t.icon;
              return (
                <Box
                  key={t.value}
                  onClick={() => {
                    if (selected) return;
                    setTypeSelect(t.value);
                    if (t.value === 'FULLTIME') setSelectedOption('Month');
                    else setSelectedOption(dayjs().date() <= 15 ? '15' : '31');
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                    transform: selected ? 'translateY(-1px)' : 'none',
                    background: selected ? t.gradient : 'transparent',
                    boxShadow: selected ? '0 6px 16px -4px rgba(0,0,0,0.35)' : 'none',
                    '&:hover': {
                      background: selected ? t.gradient : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'),
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 17, color: selected ? '#fff' : 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 500, color: selected ? '#fff' : 'text.secondary' }}>
                    {t.label}
                  </Typography>
                </Box>
              );
            })}
          </Paper>
        )}

        <div style={{ ...S.row, ...mobileRow, justifySelf: isMobile ? 'start' : 'end' }}>
          {!lockPeriod && (
            <button
              style={{ ...S.chip, ...mobileTap, opacity: isDataLoading ? 0.6 : 1, cursor: isDataLoading ? 'default' : 'pointer' }}
              onClick={() => !isDataLoading && refreshAll(dateStart, dateEnd, typeSelect, true)}
              disabled={isDataLoading}
              title="โหลดข้อมูลงวดนี้ใหม่ (ไม่ใช้ข้อมูลที่แคชไว้)"
            >
              <RefreshIcon style={{ fontSize: 14 }} />
              {isDataLoading ? 'กำลังโหลด...' : 'รีเฟรช'}
            </button>
          )}

          {isPeriodLocked && lockedSalaryList?._id && (
            <button onClick={() => setShowSalaryDetailModal(true)} style={{ ...S.btnSecondary, ...mobileTap }}>
              <VisibilityIcon style={{ fontSize: 16 }} />
              ดูรายละเอียดเงินเดือน
            </button>
          )}

          {!isTableLoading && !isDataLoading && (
            <button onClick={() => setShowOtSummaryModal(true)} style={{ ...S.btnSecondary, ...mobileTap }}>
              <AccessTimeIcon style={{ fontSize: 16 }} />
              สรุป โอที/การลงเวลา
            </button>
          )}

          {/* ปุ่มสรุปการเบิก CRL — โชว์เฉพาะเมื่องวดที่กำลังดูมีรายการเบิก CRL จริง (ไม่มีก็ซ่อนปุ่มไปเลย) */}
          {!isTableLoading && !isDataLoading && crlRows.length > 0 && (
            <button onClick={() => setShowCrlSummaryModal(true)} style={{ ...S.btnSecondary, ...mobileTap }}>
              <CalendarMonthRoundedIcon style={{ fontSize: 16 }} />
              สรุปการเบิก CRL
            </button>
          )}

          {me?.userData?.permissions?.hr_management && (
            <button onClick={handlePrint} style={{ ...S.btnPrimary, ...mobileTap }}>
              <PrintIcon style={{ fontSize: 16 }} />
              พิมพ์
            </button>
          )}

          {me?.userData?.permissions?.hr_management && (
            <button onClick={handlePrintFull} style={{ ...S.btnSecondary, ...mobileTap }} title="พิมพ์ตารางเงินเดือน + ต้นทุนตามโครงการ ครบทุกตัวเลขเหมือนหน้าจอ">
              <PrintIcon style={{ fontSize: 16 }} />
              พิมพ์ (ฉบับเต็ม)
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderReportContent = () => (
    <div style={S.panel}>
      <div style={{ ...S.panelHead, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr', gap: isMobile ? 6 : undefined }}>
        <span style={{ fontWeight: 500, fontSize: '1rem' }}>
          รายงานข้อมูล
          {selectedOption === 'Month' ? 'ทั้งเดือน' : selectedOption === '15' ? ' 1-15' : ` 16-${daysInMonth}`}{' '}
          {MONTH_NAMES[month]} {year}
        </span>

        {!lockPeriod && (
          <div style={S.row}>
            <button
              onClick={handlePreviousMonth}
              disabled={navBusy}
              style={{ ...S.btnIcon, opacity: navBusy ? 0.4 : 1 }}
              title={typeSelect === 'DAILY' ? 'งวดก่อน' : 'เดือนก่อน'}
            >
              <KeyboardArrowLeftIcon style={{ fontSize: 18 }} />
            </button>
            <button
              onClick={() => { setMonth(dayjs().month()); setYear(dayjs().year()); }}
              style={S.btnIcon} title="เดือนนี้"
            >
              <TodayIcon style={{ fontSize: 18 }} />
            </button>
            <button
              onClick={handleNextMonth}
              disabled={navBusy}
              style={{ ...S.btnIcon, opacity: navBusy ? 0.4 : 1 }}
              title={typeSelect === 'DAILY' ? 'งวดถัดไป' : 'เดือนถัดไป'}
            >
              <KeyboardArrowRightIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}

      </div>

      {me?.userData?.permissions?.hr_management && (
        <div style={{ padding: 8, borderBottom: `1px solid ${DIV}` }}>
          <AttendanceRulesPanel
            info={info}
            showFormulas={showFormulas}
            onShowFormulasChange={setShowFormulas}
            salaryMode={salaryMode}
            onSalaryModeChange={setSalaryMode}
            showFieldAllowanceMode
            fieldAllowanceModeLocked={isPeriodLocked}
            fieldAllowanceMode={faCalcMode}
            onFieldAllowanceModeChange={handleFaCalcModeChange}
            onSave={(payload) => {
              dispatch(actions.editOneInformation(info?._id, payload))
                .then(() => dispatch(actions.getInformation({})))
                .then(() => dispatch(actions.timestampPayrollFetchBoth({ dateStart, dateEnd, typeSelect })));
            }}
          />
        </div>
      )}

      <div>
        <MergedPayrollTableV2
          timestamp={timestamp}
          month={month}
          year={year}
          daysInMonth={daysInMonth}
          days={days}
          selectedOption={selectedOption}
          permission={me?.userData?.permissions}
          me={me?.userData}
          onSalaryOverride={onSalaryOverride}
          onSkipLateDeduct={onSkipLateDeduct}
          onCheckInTimeUpdate={onCheckInTimeUpdate}
          onCheckInTimeClear={onCheckInTimeClear}
          onProjectUpdate={onProjectUpdate}
          onProjectClear={onProjectClear}
          onHourOverride={onHourOverride}
          onOtOverride={onOtOverride}
          projectList={project?.rows || []}
          onRefreshTimestamp={refreshTimestampOnly}
          isLoading={isTableLoading || isDataLoading}
          dataVersion={tableDataVersion}
          manualWorkData={manualRows}
          leaveData={leaveRows}
          onLeaveWageChanged={() => hrReportDataCache.clear()}
          onManualSave={onManualSave}
          onManualDelete={onManualDelete}
          fields={salaryData}
          control={control}
          getValues={getValues}
          setValue={setValue}
          onOpenCheckinModal={handleOpenModal}
          typeSelect={typeSelect}
          attendanceRules={attendanceRules}
          attendanceRulesLoaded={attendanceRulesLoaded}
          onAdjSave={onAdjSave}
          employees={employees?.rows || []}
          showFormulas={showFormulas}
          salaryMode={salaryMode}
          readOnly={effectiveReadOnly}
          // งวดที่ยังไม่ล็อกต้อง sync ค่าคำนวณอัตโนมัติ (ปกส./ค่าแรง/ฯลฯ) ลงฟอร์มเสมอ แม้เป็น instance ซ่อนแบบ lockPeriod
          // (ที่ readOnly=true ตลอด) — ไม่งั้นยอดที่ส่งไปทำ PDF ใบสรุปค่าแรงจะไม่ตรงกับหน้าจอจริง
          autoSyncValues={!isPeriodLocked}
          onProjectDashboardChange={setProjectDashboard}
          onEmpSummaryChange={setEmpSummary}
          holidayDaySet={holidayDaySet}
          fieldAllowanceMode={faCalcMode}
          carryoverByEmpId={carryoverByEmpId}
          carryoverOutByEmpId={carryoverOutByEmpId}
          onOpenCarryover={handleOpenCarryover}
          formulaExplainOpen={showFormulaExplain}
          onFormulaExplainClose={() => { setShowFormulaExplain(false); setExplainFocus(null); }}
          formulaExplainFocus={explainFocus}
          onFormulaExplainClearFocus={() => setExplainFocus(null)}
        />

        {me?.userData?.permissions?.hr_management && (
          <div style={{ padding: 16, borderTop: `1px solid ${DIV}` }}>
            <textarea
              placeholder="หมายเหตุ"
              rows={3}
              value={note}
              disabled={effectiveReadOnly}
              onChange={(e) => setNote(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                borderRadius: 4, border: `1px solid ${DIV}`,
                fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit',
                opacity: effectiveReadOnly ? 0.6 : 1,
              }}
            />
            {effectiveReadOnly && (
              <div style={{ marginTop: 8, fontSize: '0.8125rem', color: '#92400e', fontWeight: 600 }}>
                🔒 งวดนี้บันทึกเงินเดือนไปแล้ว — ไม่สามารถบันทึกซ้ำได้
              </div>
            )}
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onSubmit}
                disabled={salaryLoading || salaryData.length === 0 || effectiveReadOnly}
                style={{
                  ...S.btnPrimary,
                  opacity: salaryLoading || salaryData.length === 0 || effectiveReadOnly ? 0.6 : 1,
                  cursor: salaryLoading || salaryData.length === 0 || effectiveReadOnly ? 'not-allowed' : 'pointer',
                }}
              >
                <SaveIcon style={{ fontSize: 16 }} />
                {effectiveReadOnly ? 'บันทึกเงินเดือนแล้ว' : salaryLoading ? 'กำลังบันทึก...' : 'บันทึกเงินเดือน'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderLeaveSummary = () => {
    const monthStart = dayjs(`${year}-${month + 1}`).startOf('month');
    const monthEnd = dayjs(`${year}-${month + 1}`).endOf('month');

    const monthLeaves = (leaveRequest?.rows || []).filter((lr) => {
      const s = dayjs(lr.start_date);
      const e = dayjs(lr.end_date);
      return s.isBefore(monthEnd) && e.isAfter(monthStart);
    });

    if (!monthLeaves.length) return null;

    const leaveTableRows = [];
    monthLeaves.forEach((lr) => {
      const empRow = (timestamp?.rows || []).find(
        (r) => String(r.employee_id) === String(lr.employee?._id || lr.employee),
      );
      // แสดงเฉพาะพนักงานรายเดือน (FULLTIME) — พนักงานรายวันไม่มีแนวคิด "ลาแล้วยังได้ค่าแรง"
      // เหมือนรายเดือน (ดู leaveAutoH ใน MergedPayrollTableV2.js ที่ cap เฉพาะ emp_type FULLTIME เช่นกัน)
      // ยึด typeSelect ของ view ปัจจุบันเป็นหลัก (เชื่อถือได้สุด เพราะ backend กรอง timestamp.rows
      // ตาม typeSelect มาให้แล้วเสมอ ไม่ต้องพึ่ง empRow?.emp_type/lr.employee?.type ที่อาจไม่ตรงกัน) —
      // typeSelect==='DAILY' → ไม่โชว์เลยทั้งงวด, ==='FULLTIME' → โชว์ทั้งงวด, ==='' (ดูรวมทุกประเภท)
      // → ค่อย fallback ไปเช็ครายคนจาก empRow/lr.employee
      const isFulltimeLeave =
        typeSelect === 'FULLTIME' ? true
        : typeSelect === 'DAILY' ? false
        : String(empRow?.emp_type ?? lr.employee?.type ?? '').toUpperCase() === 'FULLTIME';
      if (!isFulltimeLeave) return;
      const empName = empRow?.name || formatEmployeeThaiName(lr.employee) || 'ไม่ระบุ';

      let d = dayjs(lr.start_date).startOf('day');
      const lEnd = dayjs(lr.end_date).startOf('day');
      while (!d.isAfter(lEnd)) {
        if (d.month() === month && d.year() === year) {
          const dayNum = d.date();
          const checkin = empRow?.checkins?.find(
            (c) => dayjs(c?.checkIn).format('D/M/YY') === `${dayNum}/${month + 1}/${year % 100}`,
          );
          const checkinHour = checkin ? dayjs(checkin.checkIn).hour() : null;
          const isHalf = lr.period !== 'FULL_DAY';
          const nonLeavePeriod = lr.period === 'MORNING' ? 'ช่วงบ่าย' : lr.period === 'AFTERNOON' ? 'ช่วงเช้า' : null;
          const isCheckinInLeavePeriod = isHalf && checkin != null
            ? (lr.period === 'MORNING' ? checkinHour < 12 : checkinHour >= 12)
            : null;

          leaveTableRows.push({ lr, empName, day: d, checkin, isHalf, nonLeavePeriod, isCheckinInLeavePeriod });
        }
        d = d.add(1, 'day');
      }
    });

    // ทุกรายการอาจถูกกรองออกหมดถ้าเป็นพนักงานรายวันล้วน — ไม่ต้องโชว์ตารางเปล่า
    if (!leaveTableRows.length) return null;

    const HEADS = ['พนักงาน', 'ประเภท', 'ช่วงเวลา', 'วันที่', 'ลงเวลาช่วงที่เหลือ', 'เวลาเข้า', 'สถานะ'];

    return (
      <div style={{ marginTop: 16, border: `1px solid ${DIV}`, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', backgroundColor: BG, borderBottom: `1px solid ${DIV}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>
            สรุปการลา — {MONTH_NAMES[month]} {year}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${DIV}`, borderRadius: 16, padding: '1px 8px', fontSize: '0.72rem' }}>
            {leaveTableRows.length} รายการ
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: BG2 }}>
                {HEADS.map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {leaveTableRows.map(({ lr, empName, day, checkin, isHalf, nonLeavePeriod, isCheckinInLeavePeriod }, idx) => {
                let statusLabel = '';
                let statusColor = 'default';
                if (!isHalf) {
                  statusLabel = checkin ? 'มีการลงเวลา (ตรวจสอบ)' : 'ลาเต็มวัน';
                  statusColor = checkin ? 'warning' : 'default';
                } else if (!checkin) {
                  statusLabel = `ไม่มีการลงเวลา${nonLeavePeriod}`;
                  statusColor = 'warning';
                } else if (isCheckinInLeavePeriod) {
                  statusLabel = 'ลงเวลาในช่วงลา ⚠';
                  statusColor = 'error';
                } else {
                  statusLabel = `ลงเวลา${nonLeavePeriod} ✓`;
                  statusColor = 'success';
                }
                const sc = STATUS_COLOR[statusColor] || STATUS_COLOR.default;

                return (
                  <tr key={idx} style={{ borderBottom: `1px solid ${DIV}` }}>
                    <td style={{ ...S.td, fontWeight: 500 }}>{empName}</td>
                    <td style={S.td}>
                      <span style={{
                        display: 'inline-block', padding: '1px 8px', borderRadius: 4,
                        fontSize: '0.72rem', fontWeight: 700,
                        color: LEAVE_COLOR_CHIP[lr.leave_type] || '#64748b',
                        border: `1px solid ${LEAVE_COLOR_CHIP[lr.leave_type] || '#64748b'}`,
                      }}>
                        {LEAVE_TYPE_LABEL[lr.leave_type] || lr.leave_type}
                      </span>
                    </td>
                    <td style={S.td}>
                      {lr.period === 'FULL_DAY' ? 'เต็มวัน' : lr.period === 'MORNING' ? 'ครึ่งวันเช้า' : 'ครึ่งวันบ่าย'}
                    </td>
                    <td style={{ ...S.tdc, fontWeight: 600 }}>{day.format('DD/MM/YY')}</td>
                    <td style={S.tdc}>
                      {!isHalf ? '—' : checkin ? (
                        <span style={{ color: isCheckinInLeavePeriod ? '#d32f2f' : '#2e7d32', fontWeight: 700, fontSize: '0.75rem' }}>
                          {isCheckinInLeavePeriod ? 'ในช่วงลา ⚠' : `${nonLeavePeriod} ✓`}
                        </span>
                      ) : (
                        <span style={{ color: '#ed6c02', fontWeight: 700, fontSize: '0.75rem' }}>ไม่มี ✗</span>
                      )}
                    </td>
                    <td style={S.td}>{checkin?.checkInTime || (isHalf ? '—' : '-')}</td>
                    <td style={S.td}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', padding: '1px 8px',
                        borderRadius: 16, fontSize: '0.7rem', height: 22,
                        border: `1px solid ${sc.borderColor}`, color: sc.color,
                      }}>
                        {statusLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── modal "สรุปการเบิก CRL" (1 แถว/1 ครั้งที่กดขอเบิก, status = SUCCESS) เปิดจากปุ่มบนแถบเครื่องมือ ──
  // ไม่มี tab ของตัวเอง — กรองตามปุ่ม "พนักงานรายวัน / พนักงานรายเดือน" (typeSelect) ด้านบนของหน้า:
  //   typeSelect = FULLTIME → พนักงานรายเดือน ทั้งเดือน
  //   นอกนั้น (DAILY)       → พนักงานรายวัน ครึ่งเดือนที่เลือก (dateStart–dateEnd เข้ารหัสช่วงมาให้แล้ว)
  const renderCrlSummaryModal = () => {
    const isMonthlyView = typeSelect === 'FULLTIME';
    const wantType = isMonthlyView ? 'FULLTIME' : 'DAILY';
    const rows = crlRows.filter((r) => r.employeeType === wantType);
    const total = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const empCount = new Set(rows.map((r) => r.employeeId || r.employeeName)).size;
    const monthYear = `${MONTH_NAMES[month]} ${year + 543}`;
    const scopeLabel = isMonthlyView
      ? `ประจำเดือน ${monthYear}`
      : `งวดที่ ${dayjs(dateStart).date() <= 15 ? 1 : 2} · ${dayjs(dateStart).format('D')}–${dayjs(dateEnd).format('D')} ${monthYear}`;
    const fmtBaht = (n) => `฿${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const gradient = isMonthlyView
      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      : 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)';
    const STATS = [
      { label: 'จำนวนรายการ', value: rows.length.toLocaleString('en-US') },
      { label: 'พนักงาน', value: `${empCount} คน` },
      { label: 'ยอดรวม', value: fmtBaht(total), accent: true },
    ];

    return (
      <WindowFrame
        open={showCrlSummaryModal}
        onClose={() => setShowCrlSummaryModal(false)}
        title="สรุปการเบิก CRL"
        subtitle={scopeLabel}
        defaultWidth={980}
        defaultHeight={640}
      >
        {!showCrlSummaryModal ? null : (
          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 2.5,
            bgcolor: '#f8fafc',
          }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
            >
              <Chip
                size="small"
                label={isMonthlyView ? 'พนักงานรายเดือน' : 'พนักงานรายวัน'}
                sx={{ fontWeight: 700, color: '#fff', background: gradient }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{scopeLabel}</Typography>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1.5,
              mb: 2.5,
              flexShrink: 0,
            }}
            >
              {STATS.map((s) => (
                <Paper
                  key={s.label}
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    letterSpacing: 0.2,
                  }}
                  >
                    {s.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    mt: 0.5,
                    color: s.accent ? '#16a34a' : 'text.primary',
                  }}
                  >
                    {s.value}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {rows.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: 'center',
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                  bgcolor: '#fff',
                }}
              >
                <Typography sx={{ fontSize: '2rem', mb: 1 }}>🗒️</Typography>
                <Typography variant="body2" color="text.secondary">
                  ไม่มีรายการเบิก CRL (จ่ายสำเร็จ) ในช่วงนี้
                </Typography>
              </Paper>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={CRL_TH_SX}>#</TableCell>
                      <TableCell sx={CRL_TH_SX}>พนักงาน</TableCell>
                      <TableCell sx={CRL_TH_SX}>โครงการ</TableCell>
                      <TableCell sx={CRL_TH_SX}>งบประมาณ</TableCell>
                      <TableCell sx={CRL_TH_SX}>วันที่กดขอเบิก</TableCell>
                      <TableCell sx={CRL_TH_SX}>รายการ</TableCell>
                      <TableCell sx={{ ...CRL_TH_SX, textAlign: 'right' }}>จำนวนเงิน</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}
                      >
                        <TableCell sx={{ ...CRL_TD_SX, color: 'text.secondary' }}>{i + 1}</TableCell>
                        <TableCell sx={{ ...CRL_TD_SX, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {r.employeeName}
                        </TableCell>
                        <TableCell sx={CRL_TD_SX}>{r.projectName || '—'}</TableCell>
                        <TableCell sx={CRL_TD_SX}>{r.budgetName || '—'}</TableCell>
                        <TableCell sx={{ ...CRL_TD_SX, whiteSpace: 'nowrap' }}>
                          {r.createdAt ? dayjs(r.createdAt).format('DD/MM/YY HH:mm') : '—'}
                        </TableCell>
                        <TableCell sx={{ ...CRL_TD_SX, minWidth: 220 }}>{r.name || '—'}</TableCell>
                        <TableCell sx={{
                          ...CRL_TD_SX,
                          textAlign: 'right',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                        >
                          {fmtBaht(r.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                      <TableCell colSpan={6} sx={{ ...CRL_TD_SX, fontWeight: 800, color: 'text.primary' }}>
                        รวมทั้งสิ้น
                      </TableCell>
                      <TableCell sx={{
                        ...CRL_TD_SX,
                        textAlign: 'right',
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        color: '#16a34a',
                      }}
                      >
                        {fmtBaht(total)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </WindowFrame>
    );
  };

  const renderModal = () => {
    if (!openModal) return null;
    return (
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 1300, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={handleCloseModal}
      >
        <div
          style={{ backgroundColor: '#fff', borderRadius: 8, padding: '24px 16px', maxHeight: '70vh', overflowY: 'auto', minWidth: isMobile ? 0 : 400, maxWidth: 'calc(100vw - 24px)', boxSizing: 'border-box', position: 'relative' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleCloseModal}
            style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, fontWeight: 700, padding: '2px 8px', color: TEXT2 }}
          >&times;</button>
          <h6 style={{ margin: '0 0 12px', fontSize: '1.1rem', fontWeight: 600 }}>รายการลงเวลา</h6>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 400, borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {['วันที่', 'โครงการ', 'เข้า', 'ออก', 'วัน', 'หมายเหตุ'].map((h) => (
                    <th key={h} style={{ ...S.th, textAlign: 'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {_.map(selectEachEmployee, (e, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${DIV}` }}>
                    <td style={{ ...S.tdc }}>{dayjs(e?.checkIn).format('DD/MM/YYYY')}</td>
                    <td style={{ ...S.tdc }}>{e?.project_number || '-'} {e?.project_name || '-'}</td>
                    <td style={{ ...S.tdc }}>{e?.checkInTime || '-'}</td>
                    <td style={{ ...S.tdc }}>{e?.checkOutTime || '-'}</td>
                    <td style={{ ...S.tdc }}>{e?.totalTime || '-'}</td>
                    <td style={{ ...S.tdc, color: e?.note ? '#ed6c02' : TEXT2 }}>{e?.note || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // หลังลบเงินเดือนสำเร็จจาก modal — ปิด modal + เช็คสถานะล็อกของงวดนี้ใหม่ (ควรกลับเป็นไม่ล็อก
  // เพราะ backend เคลียร์ status_payroll ของ checkin ทั้งหมดในงวดนี้ให้แล้ว) แล้ว refetch ข้อมูลสด
  const handleSalaryDeleted = async () => {
    setShowSalaryDetailModal(false);
    setLockedSalaryByEmpId(null);
    try {
      const res = await dispatch(actions.salaryListCheckPeriod({ dateStart, dateEnd, role_type: typeSelect }));
      setIsPeriodLocked(!!res?.exists);
    } catch {
      setIsPeriodLocked(false);
    }
    await refreshAll(dateStart, dateEnd, typeSelect, true);
  };

  // เปิดเป็น modal แทนการเด้งไปหน้า /humen/salarylist/detail/:id — ใช้ id ของ salaries
  // ที่บันทึกไปแล้วของงวดนี้ (lockedSalaryList._id) ป้อนให้ HrSalaryDetail แทน useParams
  const renderSalaryDetailModal = () => (
    <WindowFrame
      open={showSalaryDetailModal}
      onClose={() => setShowSalaryDetailModal(false)}
      title="รายละเอียดเงินเดือน"
      subtitle={`${MONTH_NAMES[month]} ${year}`}
      defaultWidth={1200}
      defaultHeight={800}
    >
      <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
        {showSalaryDetailModal && lockedSalaryList?._id && (
          <HrSalaryDetail id={lockedSalaryList._id} onDeleted={handleSalaryDeleted} />
        )}
      </div>
    </WindowFrame>
  );

  // สรุป OT/การลงเวลา ของ period ที่กำลังดูอยู่ — รับ timestamp/manualRows/days ตรงจาก state ที่
  // fetch ไว้แล้วสำหรับ MergedPayrollTableV2 ด้านล่าง ไม่ยิง API ใหม่ ไม่คำนวณช่วงวันที่เอง
  const renderOtSummaryModal = () => (
    <WindowFrame
      open={showOtSummaryModal}
      onClose={() => setShowOtSummaryModal(false)}
      title="สรุป โอที/การลงเวลา"
      subtitle={`${MONTH_NAMES[month]} ${year}`}
      defaultWidth={1200}
      defaultHeight={800}
    >
      <div style={{
        padding: '12px 20px', overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex',
      }}
      >
        {showOtSummaryModal && (
          <OtTimestampSummaryTable
            timestamp={timestamp}
            manualWorkData={manualRows}
            days={days}
            month={month}
            year={year}
            holidayDaySet={holidayDaySet}
          />
        )}
      </div>
    </WindowFrame>
  );

  if (timestamp.loading || !timestamp.isCompleted) {
    return <Loading isLoading />;
  }

  return (
    <div>
      {renderCompactHeader()}
      {renderReportContent()}
      {/* งวดล็อกแล้ว → ใช้ displayProjectDashboard (snapshot ที่บันทึกจริงตอนกดบันทึกเงินเดือน) แทน
          projectDashboard สด ดูเหตุผล/ข้อแลกเปลี่ยนที่ประกาศ displayProjectDashboard ด้านบน */}
      <div>
        <TimestampProjectCost
          timestamp={{ dashboard: displayProjectDashboard }}
          laborStatuses={laborStatuses}
          onOpenFormulaExplain={!lockPeriod && !isTableLoading && !isDataLoading && salaryData.length > 0 ? () => { setExplainFocus(null); setShowFormulaExplain(true); } : undefined}
          onOpenProjectFormula={!lockPeriod && !isTableLoading && !isDataLoading && salaryData.length > 0
            ? (row) => {
              setExplainFocus({ key: row.project_id ? String(row.project_id) : 'no_project', external: !!row.external, number: row.project_number || '' });
              setShowFormulaExplain(true);
            }
            : undefined}
        />
      </div>
      {/* ตาราง "สรุปการลา" ย้ายมาไว้ล่างสุด (หลังต้นทุนตามโครงการ) — แสดงเฉพาะพนักงานรายเดือน
          (FULLTIME) เพราะพนักงานรายวันไม่มีแนวคิด "ลาแล้วยังได้ค่าแรง" แบบพนักงานรายเดือน */}
      {renderLeaveSummary()}
      {renderModal()}
      {renderSalaryDetailModal()}
      {renderOtSummaryModal()}
      {renderCrlSummaryModal()}
      <CarryoverDialog
        open={!!carryoverEmp}
        onClose={() => setCarryoverEmp(null)}
        employee={carryoverEmp}
        roleType={typeSelect}
        currentPeriod={{ dateStart, dateEnd }}
        periodLabel={`${MONTH_NAMES[month]} ${year}`}
        readOnly={effectiveReadOnly}
        onChanged={refetchCarryover}
      />
      <CarryoverPromptDialog
        open={!!carryoverPrompt}
        list={carryoverPrompt?.list || []}
        nextPeriodLabel={(() => {
          const np = getAdjacentPeriod(1);
          const half = np.selectedOption === '15' ? ' 1-15' : np.selectedOption === '31' ? ' 16-สิ้นเดือน' : '';
          return `${MONTH_NAMES[np.month]} ${np.year}${half}`;
        })()}
        onConfirm={(optIn) => { carryoverPrompt?.resolve(optIn); setCarryoverPrompt(null); }}
        onCancel={() => { carryoverPrompt?.resolve(null); setCarryoverPrompt(null); }}
      />
    </div>
  );
}
